// pages/Team/Team.jsx
import { useState, useEffect } from "react";
import { ArrowRight, Linkedin, X } from "lucide-react";
import "../../index.css";
import "./Team.css";

import { getTeamMembers } from "../../Components/Lib/contentful";

/* ————————————————— COMPONENTS ————————————————— */

const TeamMemberCard = ({ member, onClick }) => (
  <div className="team-member-card" onClick={() => onClick(member)}>
    <div
      className="member-image"
      style={{
        backgroundImage: member.image ? `url(${member.image})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    />
    <div className="member-info">
      <h3 className="member-name">{member.name}</h3>
      <p className="member-role">{member.role}</p>
      <button className="learn-more-btn">
        Read more <ArrowRight size={16} />
      </button>
    </div>
  </div>
);

const MemberModal = ({ member, isOpen, onClose }) => {
  if (!isOpen || !member) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <X size={32} />
        </button>

        <div
          className="modal-image"
          style={{
            backgroundImage: member.image ? `url(${member.image})` : "none",
          }}
        />

        <div className="modal-info">
          <h2>{member.name}</h2>
          <h3 className="modal-role">{member.role}</h3>
          <p className="modal-bio">{member.bio}</p>

          <div className="modal-socials">
            {member.social?.linkedin && (
              <a
                href={member.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Linkedin size={28} />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ————————————————— TEAM PAGE ————————————————— */

const Team = () => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchTeam() {
      try {
        setLoading(true);
        const members = await getTeamMembers();

        // Sort all members by index (smaller index = higher rank)
        const sortedMembers = [...members].sort((a, b) => {
          const indexA = a?.index ?? Infinity;
          const indexB = b?.index ?? Infinity;
          return indexA - indexB;
        });

        setTeamMembers(sortedMembers);
      } catch (err) {
        console.error("Failed to fetch team from Contentful:", err);
        setError("Failed to load team members. Please try again later.");
      } finally {
        setLoading(false);
      }
    }

    fetchTeam();
  }, []);

  /* ---------------- CATEGORY NORMALIZATION ---------------- */

  const grouped = teamMembers.reduce((acc, member) => {
    let cat = (member.category || "").toLowerCase().trim();

    if (cat.includes("board")) cat = "board";
    else if (cat.includes("advisor")) cat = "advisors";
    else if (cat.includes("leader")) cat = "leadership";
    else if (cat.includes("senior") || cat.includes("associate")) cat = "senior";
    else cat = "other";

    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(member);

    return acc;
  }, {});

  // Sort each group independently by index (smaller index first)
  Object.keys(grouped).forEach((key) => {
    grouped[key].sort((a, b) => {
      const indexA = a?.index ?? Infinity;
      const indexB = b?.index ?? Infinity;
      return indexA - indexB;
    });
  });

  /* ---------------- Loader ---------------- */

  const Loader = () => (
    <div className="team-loader">
      <div className="spinner"></div>
      <p>Loading our amazing team...</p>
    </div>
  );

  if (loading) return <Loader />;

  if (error) {
    return (
      <div className="error-message text-center" style={{ padding: "100px 20px" }}>
        <h2>Oops!</h2>
        <p>{error}</p>
      </div>
    );
  }

  //console.log("Grouped team members:", grouped);

  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <div className="container text-center">
          <h1>Our Team</h1>
          <p style={{ color: "#000000" }}>
            A multidisciplinary team of researchers, data scientists, MERL experts, and consultants committed to local ownership, evidence-based practice, and inclusive development in Zambia and beyond.
          </p>

          <div className="hero-buttons">
            <a href="#board" className="btn btn-primary">
              Meet Our Leadership <ArrowRight />
            </a>

            <a href="#seniors" className="btn btn-secondary">
              Thematic Experts
            </a>

            <a href="#leadership" className="btn btn-primary">
              Management <ArrowRight />
            </a>
          </div>
        </div>
      </section>

      {/* Board */}
      {grouped.board?.length > 0 && (
        <section id="board" className="section">
          <div className="container text-center">
            <h2>Board of Directors</h2>

            <div className="team-grid modern-grid">
              {grouped.board.map((member) => (
                <TeamMemberCard
                  key={member.id}
                  member={member}
                  onClick={setSelectedMember}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Advisors */}
      {grouped.advisors?.length > 0 && (
        <section id="advisors" className="section bg-light">
          <div className="container text-center">
            <h2>Advisors to the Board</h2>

            <div className="team-grid modern-grid">
              {grouped.advisors.map((member) => (
                <TeamMemberCard
                  key={member.id}
                  member={member}
                  onClick={setSelectedMember}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Leadership */}
      {grouped.leadership?.length > 0 && (
        <section id="leadership" className="section">
          <div className="container text-center">
            <h2>Our Management Team</h2>

            <div className="team-grid modern-grid">
              {grouped.leadership.map((member) => (
                <TeamMemberCard
                  key={member.id}
                  member={member}
                  onClick={setSelectedMember}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Senior & Associate Consultants */}
      {grouped.senior?.length > 0 && (
        <section id="seniors" className="section bg-light">
          <div className="container text-center">
            <h2>Thematic Experts</h2>

            <div className="team-grid modern-grid">
              {grouped.senior.map((member) => (
                <TeamMemberCard
                  key={member.id}
                  member={member}
                  onClick={setSelectedMember}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="final-cta text-center">
        <div className="container">
          <h2>
            Inspired by Our Team?
            <br />
            Let's Collaborate on Impactful Research.
          </h2>

          <a href="/contact" className="btn btn-primary">
            Get in Touch <ArrowRight size={28} />
          </a>
        </div>
      </section>

      {/* Modal */}
      <MemberModal
        member={selectedMember}
        isOpen={!!selectedMember}
        onClose={() => setSelectedMember(null)}
      />

      {/* Footer */}
      <footer className="footer">
        <div className="footer-grid container">
          <div>
            <div className="footer-logo">DevLearn Impact Research Ltd</div>
            <p>Plot No. 3788/A, Avondale Road, Lusaka</p>
            <p>+260 973 902 981 / +260 978 341 825</p>
            <p>info@devlearnimpact.com</p>
          </div>

          <div>
            <h4>Services</h4>
            <ul>
              <li><a href="/services">Applied Research</a></li>
              <li><a href="/services">MERL Frameworks</a></li>
              <li><a href="/services">Capacity Building</a></li>
            </ul>
          </div>

          <div>
            <h4>Thematic Areas</h4>
            <ul>
              <li><a href="/sectors">Education & Health</a></li>
              <li><a href="/sectors">Governance & Climate</a></li>
              <li><a href="/sectors">Economic Inclusion</a></li>
            </ul>
          </div>

          <div>
            <h4>Company</h4>
            <ul>
              <li><a href="/about">About</a></li>
              <li><a href="/team">Team</a></li>
              <li><a href="/case-studies">Projects</a></li>
            </ul>
          </div>

          <div>
            <h4>Connect</h4>
            <ul>
              <li>
                <a href="https://www.linkedin.com/company/devlearn-impact-research/">
                  LinkedIn
                </a>
              </li>
              <li>
                <a href="https://www.facebook.com/share/1AS3Ahfsy7/">
                  Facebook
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="copyright">
          © 2026 DevLearn Impact Research Ltd. All rights reserved.
        </div>
      </footer>
    </>
  );
};

export default Team;
// // pages/Team/Team.jsx
// import { useState, useEffect } from "react";
// import { ArrowRight, Linkedin, X } from "lucide-react";
// import "../../index.css";
// import "./Team.css";

// import { getTeamMembers } from "../../Components/Lib/contentful";

// /* ————————————————— COMPONENTS ————————————————— */

// const TeamMemberCard = ({ member, onClick }) => (
//   <div className="team-member-card" onClick={() => onClick(member)}>
//     <div
//       className="member-image"
//       style={{
//         backgroundImage: member.image ? `url(${member.image})` : "none",
//         backgroundSize: "cover",
//         backgroundPosition: "center",
//       }}
//     />
//     <div className="member-info">
//       <h3 className="member-name">{member.name}</h3>
//       <p className="member-role">{member.role}</p>
//       <button className="learn-more-btn">
//         Read more <ArrowRight size={16} />
//       </button>
//     </div>
//   </div>
// );

// const MemberModal = ({ member, isOpen, onClose }) => {
//   if (!isOpen || !member) return null;

//   return (
//     <div className="modal-overlay" onClick={onClose}>
//       <div className="modal-content" onClick={(e) => e.stopPropagation()}>
//         <button className="modal-close" onClick={onClose}>
//           <X size={32} />
//         </button>

//         <div
//           className="modal-image"
//           style={{
//             backgroundImage: member.image ? `url(${member.image})` : "none",
//           }}
//         />

//         <div className="modal-info">
//           <h2>{member.name}</h2>
//           <h3 className="modal-role">{member.role}</h3>
//           <p className="modal-bio">{member.bio}</p>

//           <div className="modal-socials">
//             {member.social?.linkedin && (
//               <a
//                 href={member.social.linkedin}
//                 target="_blank"
//                 rel="noopener noreferrer"
//               >
//                 <Linkedin size={28} />
//               </a>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// /* ————————————————— TEAM PAGE ————————————————— */

// const Team = () => {
//   const [selectedMember, setSelectedMember] = useState(null);
//   const [teamMembers, setTeamMembers] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     async function fetchTeam() {
//       try {
//         setLoading(true);
//         const members = await getTeamMembers();
//         setTeamMembers(members);
//       } catch (err) {
//         console.error("Failed to fetch team from Contentful:", err);
//         setError("Failed to load team members. Please try again later.");
//       } finally {
//         setLoading(false);
//       }
//     }

//     fetchTeam();
//   }, []);

//   /* ---------------- CATEGORY NORMALIZATION ---------------- */

//   const grouped = teamMembers.reduce((acc, member) => {
//     let cat = (member.category || "").toLowerCase().trim();

//     if (cat.includes("board")) cat = "board";
//     else if (cat.includes("advisor")) cat = "advisors";
//     else if (cat.includes("leader")) cat = "leadership";
//     else if (cat.includes("senior") || cat.includes("associate")) cat = "senior";
//     else cat = "other";

//     if (!acc[cat]) acc[cat] = [];
//     acc[cat].push(member);

//     return acc;
//   }, {});

//   /* ---------------- Loader ---------------- */

//   const Loader = () => (
//     <div className="team-loader">
//       <div className="spinner"></div>
//       <p>Loading our amazing team...</p>
//     </div>
//   );

//   if (loading) return <Loader />;

//   if (error) {
//     return (
//       <div className="error-message text-center" style={{ padding: "100px 20px" }}>
//         <h2>Oops!</h2>
//         <p>{error}</p>
//       </div>
//     );
//   }

//   //console.log("Grouped team members:", grouped);

//   return (
//     <>
//       {/* Hero Section */}
//       <section className="hero">
//         <div className="container text-center">
//           <h1>Our Team</h1>
//           <p style={{ color: "#000000" }}>
//             A multidisciplinary team of researchers, data scientists, MERL experts, and consultants committed to local ownership, evidence-based practice, and inclusive development in Zambia and beyond.
//           </p>

//           <div className="hero-buttons">
//             <a href="#board" className="btn btn-primary">
//               Meet Our Leadership <ArrowRight />
//             </a>

//             <a href="#seniors" className="btn btn-secondary">
//               Thematic Experts
//             </a>

//             <a href="#leadership" className="btn btn-primary">
//               Management <ArrowRight />
//             </a>
//           </div>
//         </div>
//       </section>

//       {/* Board */}
//       {grouped.board?.length > 0 && (
//         <section id="board" className="section">
//           <div className="container text-center">
//             <h2>Board of Directors</h2>

//             <div className="team-grid modern-grid">
//               {grouped.board.map((member) => (
//                 <TeamMemberCard
//                   key={member.id}
//                   member={member}
//                   onClick={setSelectedMember}
//                 />
//               ))}
//             </div>
//           </div>
//         </section>
//       )}

//       {/* Advisors */}
//       {grouped.advisors?.length > 0 && (
//         <section id="advisors" className="section bg-light">
//           <div className="container text-center">
//             <h2>Advisors to the Board</h2>

//             <div className="team-grid modern-grid">
//               {grouped.advisors.map((member) => (
//                 <TeamMemberCard
//                   key={member.id}
//                   member={member}
//                   onClick={setSelectedMember}
//                 />
//               ))}
//             </div>
//           </div>
//         </section>
//       )}

//       {/* Leadership */}
//       {grouped.leadership?.length > 0 && (
//         <section id="leadership" className="section">
//           <div className="container text-center">
//             <h2>Our Management Team</h2>

//             <div className="team-grid modern-grid">
//               {grouped.leadership.map((member) => (
//                 <TeamMemberCard
//                   key={member.id}
//                   member={member}
//                   onClick={setSelectedMember}
//                 />
//               ))}
//             </div>
//           </div>
//         </section>
//       )}

//       {/* Senior & Associate Consultants */}
//       {grouped.senior?.length > 0 && (
//         <section id="seniors" className="section bg-light">
//           <div className="container text-center">
//             <h2>Thematic Experts</h2>

//             <div className="team-grid modern-grid">
//               {grouped.senior.map((member) => (
//                 <TeamMemberCard
//                   key={member.id}
//                   member={member}
//                   onClick={setSelectedMember}
//                 />
//               ))}
//             </div>
//           </div>
//         </section>
//       )}

//       {/* CTA */}
//       <section className="final-cta text-center">
//         <div className="container">
//           <h2>
//             Inspired by Our Team?
//             <br />
//             Let's Collaborate on Impactful Research.
//           </h2>

//           <a href="/contact" className="btn btn-primary">
//             Get in Touch <ArrowRight size={28} />
//           </a>
//         </div>
//       </section>

//       {/* Modal */}
//       <MemberModal
//         member={selectedMember}
//         isOpen={!!selectedMember}
//         onClose={() => setSelectedMember(null)}
//       />

//       {/* Footer */}
//       <footer className="footer">
//         <div className="footer-grid container">
//           <div>
//             <div className="footer-logo">DevLearn Impact Research Ltd</div>
//             <p>Plot No. 3788/A, Avondale Road, Lusaka</p>
//             <p>+260 973 902 981 / +260 978 341 825</p>
//             <p>info@devlearnimpact.com</p>
//           </div>

//           <div>
//             <h4>Services</h4>
//             <ul>
//               <li><a href="/services">Applied Research</a></li>
//               <li><a href="/services">MERL Frameworks</a></li>
//               <li><a href="/services">Capacity Building</a></li>
//             </ul>
//           </div>

//           <div>
//             <h4>Thematic Areas</h4>
//             <ul>
//               <li><a href="/sectors">Education & Health</a></li>
//               <li><a href="/sectors">Governance & Climate</a></li>
//               <li><a href="/sectors">Economic Inclusion</a></li>
//             </ul>
//           </div>

//           <div>
//             <h4>Company</h4>
//             <ul>
//               <li><a href="/about">About</a></li>
//               <li><a href="/team">Team</a></li>
//               <li><a href="/case-studies">Projects</a></li>
//             </ul>
//           </div>

//           <div>
//             <h4>Connect</h4>
//             <ul>
//               <li>
//                 <a href="https://www.linkedin.com/company/devlearn-impact-research/">
//                   LinkedIn
//                 </a>
//               </li>
//               <li>
//                 <a href="https://www.facebook.com/share/1AS3Ahfsy7/">
//                   Facebook
//                 </a>
//               </li>
//             </ul>
//           </div>
//         </div>

//         <div className="copyright">
//           © 2026 DevLearn Impact Research Ltd. All rights reserved.
//         </div>
//       </footer>
//     </>
//   );
// };

// export default Team;
// // pages/Team/Team.jsx
// import { useState, useEffect } from "react";
// import { ArrowRight, Linkedin, X } from "lucide-react";
// import "../../index.css";
// import "./Team.css";

// //import { getTeamMembers } from "../../lib/contentful"; // ← Adjust path if your folder structure is different
// import { getTeamMembers } from "../../Components/Lib/contentful"; // ← Adjust path if your folder structure is different
// /* ————————————————— COMPONENTS ————————————————— */

// const TeamMemberCard = ({ member, onClick }) => (
//   <div className="team-member-card" onClick={() => onClick(member)}>
//     <div
//       className="member-image"
//       style={{
//         backgroundImage: member.image ? `url(${member.image})` : "none",
//         backgroundSize: "cover",
//         backgroundPosition: "center",
//       }}
//     />
//     <div className="member-info">
//       <h3 className="member-name">{member.name}</h3>
//       <p className="member-role">{member.role}</p>
//       <button className="learn-more-btn">
//         Read more <ArrowRight size={16} />
//       </button>
//     </div>
//   </div>
// );

// const MemberModal = ({ member, isOpen, onClose }) => {
//   if (!isOpen || !member) return null;

//   return (
//     <div className="modal-overlay" onClick={onClose}>
//       <div className="modal-content" onClick={(e) => e.stopPropagation()}>
//         <button className="modal-close" onClick={onClose}>
//           <X size={32} />
//         </button>

//         <div
//           className="modal-image"
//           style={{
//             backgroundImage: member.image ? `url(${member.image})` : "none",
//           }}
//         />

//         <div className="modal-info">
//           <h2>{member.name}</h2>
//           <h3 className="modal-role">{member.role}</h3>
//           <p className="modal-bio">{member.bio}</p>

//           <div className="modal-socials">
//             {member.social?.linkedin && (
//               <a
//                 href={member.social.linkedin}
//                 target="_blank"
//                 rel="noopener noreferrer"
//               >
//                 <Linkedin size={28} />
//               </a>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// /* ————————————————— TEAM PAGE ————————————————— */

// const Team = () => {
//   const [selectedMember, setSelectedMember] = useState(null);
//   const [teamMembers, setTeamMembers] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     async function fetchTeam() {
//       try {
//         setLoading(true);
//         const members = await getTeamMembers();
//         setTeamMembers(members);
//       } catch (err) {
//         console.error("Failed to fetch team from Contentful:", err);
//         setError("Failed to load team members. Please try again later.");
//       } finally {
//         setLoading(false);
//       }
//     }

//     fetchTeam();
//   }, []);

//   // Group members by category
//   const grouped = teamMembers.reduce((acc, member) => {
//     const cat = (member.category || "other").toLowerCase().trim();
//     if (!acc[cat]) acc[cat] = [];
//     acc[cat].push(member);
//     return acc;
//   }, {});

//   // Simple Loader Component
//   const Loader = () => (
//     <div className="team-loader">
//       <div className="spinner"></div>
//       <p>Loading our amazing team...</p>
//     </div>
//   );

//   if (loading) {
//     return <Loader />;
//   }

//   if (error) {
//     return (
//       <div className="error-message text-center" style={{ padding: "100px 20px" }}>
//         <h2>Oops!</h2>
//         <p>{error}</p>
//       </div>
//     );
//   }

//   console.log("Grouped team members:", grouped); // Debugging log

//   return (
//     <>
//       {/* Hero Section - Unchanged */}
//       <section className="hero">
//         <div className="container text-center">
//           <h1>Our Team</h1>
//           <p style={{ color: "#000000" }}>
//             A multidisciplinary team of researchers, data scientists, MERL experts, and consultants committed to local ownership, evidence-based practice, and inclusive development in Zambia and beyond.
//           </p>
//           <div className="hero-buttons">
//             <a href="#board" className="btn btn-primary">
//               Meet Our Leadership <ArrowRight />
//             </a>
//             <a href="#seniors" className="btn btn-secondary">
//               Thematic Experts
//             </a>
//             <a href="#leadership" className="btn btn-primary">
//               Management <ArrowRight />
//             </a>
//           </div>
//         </div>
//       </section>

//       {/* Board of Directors */}
//       {grouped.board && grouped.board.length > 0 && (
//         <section id="board" className="section">
//           <div className="container text-center">
//             <h2>Board of Directors</h2>
//             <div className="team-grid modern-grid">
//               {grouped.board.map((member) => (
//                 <TeamMemberCard
//                   key={member.id}
//                   member={member}
//                   onClick={setSelectedMember}
//                 />
//               ))}
//             </div>
//           </div>
//         </section>
//       )}

//          {/* Fallback for any other categories */}
//       {Object.keys(grouped).some(
//         (cat) => !["board", "advisors", "leadership", "senior", "other"].includes(cat)
//       ) && (
//         <section className="section">
//           <div className="container text-center">
//             <h2>Board of Directors</h2>
//             <div className="team-grid modern-grid">
//               {teamMembers
//                 .filter(
//                   (m) =>
//                     !["board", "advisors", "leadership", "senior"].includes(
//                       (m.category || "").toLowerCase()
//                     )
//                 )
//                 .map((member) => (
//                   <TeamMemberCard
//                     key={member.id}
//                     member={member}
//                     onClick={setSelectedMember}
//                   />
//                 ))}
//             </div>
//           </div>
//         </section>
//       )}

//       {/* Advisors */}
//       {grouped.advisors && grouped.advisors.length > 0 && (
//         <section id="advisors" className="section bg-light">
//           <div className="container text-center">
//             <h2>Advisors to the Board</h2>
//             <div className="team-grid modern-grid">
//               {grouped.advisors.map((member) => (
//                 <TeamMemberCard
//                   key={member.id}
//                   member={member}
//                   onClick={setSelectedMember}
//                 />
//               ))}
//             </div>
//           </div>
//         </section>
//       )}

//       {/* Management / Leadership */}
//       {grouped.leadership && grouped.leadership.length > 0 && (
//         <section id="leadership" className="section">
//           <div className="container text-center">
//             <h2>Our Management Team</h2>
//             <div className="team-grid modern-grid">
//               {grouped.leadership.map((member) => (
//                 <TeamMemberCard
//                   key={member.id}
//                   member={member}
//                   onClick={setSelectedMember}
//                 />
//               ))}
//             </div>
//           </div>
//         </section>
//       )}

//       {/* Thematic Experts / Senior Associates */}
//       {grouped?.senior && grouped.senior?.length > 0 && (
//         //echo the grup name in the heading
//         console.log("here",grouped.senior[0].category),
//         <section id="seniors" className="section bg-light">
//           <div className="container text-center">
//             <h2>Thematic Experts</h2>
//             <div className="team-grid modern-grid">
//               {grouped.senior.map((member) => (
//                 <TeamMemberCard
//                   key={member.id}
//                   member={member}
//                   onClick={setSelectedMember}
//                 />
//               ))}
//             </div>
//           </div>
//         </section>
//       )
    
//       }

//        {/* Advisors */}
//       {grouped.advisors && grouped.advisors.length > 0 && (
//         <section id="advisors" className="section bg-light">
//           <div className="container text-center">
//             <h2>Thematic Experts</h2>
//             <div className="team-grid modern-grid">
//               {grouped.advisors.map((member) => (
//                 <TeamMemberCard
//                   key={member.id}
//                   member={member}
//                   onClick={setSelectedMember}
//                 />
//               ))}
//             </div>
//           </div>
//         </section>
//       )}

   

//       {/* CTA Section */}
//       <section className="final-cta text-center">
//         <div className="container">
//           <h2>Inspired by Our Team?<br />Let's Collaborate on Impactful Research.</h2>
//           <a href="/contact" className="btn btn-primary">
//             Get in Touch <ArrowRight size={28} />
//           </a>
//         </div>
//       </section>

//       {/* Modal */}
//       <MemberModal
//         member={selectedMember}
//         isOpen={!!selectedMember}
//         onClose={() => setSelectedMember(null)}
//       />

//       {/* Footer - Unchanged */}
//       <footer className="footer">
//         <div className="footer-grid container">
//           <div>
//             <div className="footer-logo">DevLearn Impact Research Ltd</div>
//             <p>Plot No. 3788/A, Avondale Road, Lusaka</p>
//             <p>+260 973 902 981 / +260 978 341 825</p>
//             <p>info@devlearnimpact.com</p>
//           </div>
//           <div>
//             <h4>Services</h4>
//             <ul>
//               <li><a href="/services">Applied Research</a></li>
//               <li><a href="/services">MERL Frameworks</a></li>
//               <li><a href="/services">Capacity Building</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Thematic Areas</h4>
//             <ul>
//               <li><a href="/sectors">Education & Health</a></li>
//               <li><a href="/sectors">Governance & Climate</a></li>
//               <li><a href="/sectors">Economic Inclusion</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Company</h4>
//             <ul>
//               <li><a href="/about">About</a></li>
//               <li><a href="/team">Team</a></li>
//               <li><a href="/case-studies">Projects</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Connect</h4>
//             <ul>
//               <li><a href="https://www.linkedin.com/company/devlearn-impact-research/">LinkedIn</a></li>
//               <li><a href="https://www.facebook.com/share/1AS3Ahfsy7/">Facebook</a></li>
//             </ul>
//           </div>
//         </div>
//         <div className="copyright">
//           © 2026 DevLearn Impact Research Ltd. All rights reserved.
//         </div>
//       </footer>
//     </>
//   );
// };

// export default Team;
// import React, { useEffect, useState } from "react";
// import "../../index.css"; // global styles
// import "./Team.css"; // page‑specific styles
// // import { client } from "./contentful.js";
// import { client } from "../../Components/Lib/contentful";
// //import { client } from "../../lib/contentful";

// function TeamMemberCard({ member, onClick }) {
//   return (
//     <div className="team-card" onClick={() => onClick(member)}>
//       <img
//         src={member.image || "/placeholder.jpg"}
//         alt={member.name}
//         className="team-image"
//       />

//       <h3>{member.name}</h3>
//       <p>{member.role}</p>
//     </div>
//   );
// }

// export default function Team() {

//   const [boardOfDirectors, setBoard] = useState([]);
//   const [advisors, setAdvisors] = useState([]);
//   const [leadership, setLeadership] = useState([]);
//   const [consultants, setConsultants] = useState([]);

//   const [selectedMember, setSelectedMember] = useState(null);
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     async function loadTeam() {
//       try {

//         const response = await client.getEntries({
//           content_type: "person",
//           limit: 200
//         });

//         const members = response.items.map(item => ({
//           name: item.fields.name,
//           role: item.fields.role,
//           bio: item.fields.bio,
//           image: item.fields.image?.fields?.file?.url
//             ? `https:${item.fields.image.fields.file.url}`
//             : null,
//           social: {
//             linkedin: item.fields.linkedin
//           },
//           category: item.fields.category
//         }));

//         setBoard(members.filter(m => m.category === "Board"));
//         setAdvisors(members.filter(m => m.category === "Advisor"));
//         setLeadership(members.filter(m => m.category === "Leadership"));
//         setConsultants(members.filter(m => m.category === "Consultant"));

//         setLoading(false);

//       } catch (error) {
//         console.error("Error loading team:", error);
//         setLoading(false);
//       }
//     }

//     loadTeam();
//   }, []);

//   if (loading) {
//     return (
//       <div className="container text-center">
//         <h2>Loading Team...</h2>
//       </div>
//     );
//   }

//   return (
//     <div className="team-page">

//       <section className="team-section">
//         <h2>Board of Directors</h2>

//         <div className="team-grid">
//           {boardOfDirectors.map((member, i) => (
//             <TeamMemberCard key={i} member={member} onClick={setSelectedMember} />
//           ))}
//         </div>
//       </section>


//       <section className="team-section">
//         <h2>Advisors</h2>

//         <div className="team-grid">
//           {advisors.map((member, i) => (
//             <TeamMemberCard key={i} member={member} onClick={setSelectedMember} />
//           ))}
//         </div>
//       </section>


//       <section className="team-section">
//         <h2>Leadership</h2>

//         <div className="team-grid">
//           {leadership.map((member, i) => (
//             <TeamMemberCard key={i} member={member} onClick={setSelectedMember} />
//           ))}
//         </div>
//       </section>


//       <section className="team-section">
//         <h2>Thematic Experts</h2>

//         <div className="team-grid">
//           {consultants.map((member, i) => (
//             <TeamMemberCard key={i} member={member} onClick={setSelectedMember} />
//           ))}
//         </div>
//       </section>


//       {selectedMember && (
//         <div className="team-modal">

//           <div className="modal-content">

//             <button
//               className="close-btn"
//               onClick={() => setSelectedMember(null)}
//             >
//               ×
//             </button>

//             <img
//               src={selectedMember.image || "/placeholder.jpg"}
//               alt={selectedMember.name}
//               className="modal-image"
//             />

//             <h2>{selectedMember.name}</h2>

//             <h4>{selectedMember.role}</h4>

//             <p>{selectedMember.bio}</p>

//             {selectedMember.social?.linkedin && (
//               <a
//                 href={selectedMember.social.linkedin}
//                 target="_blank"
//                 rel="noreferrer"
//               >
//                 LinkedIn
//               </a>
//             )}

//           </div>

//         </div>
//       )}

//     </div>
//   );
// }

// // pages/Team/Team.jsx
// import { useState } from "react";
// import { ArrowRight, Linkedin, Twitter, Mail, X } from "lucide-react";
// import "../../index.css"; // global styles
// import "./Team.css"; // page‑specific styles

// import Img4 from "../../Images/Mwale.png";
// import Img5 from "../../Images/image8.png";
// import Img6 from "../../Images/image12.png";
// import Img7 from "../../Images/ben.png";
// import Img8 from "../../Images/image60.png";
// import Img9 from "../../Images/image16.png";
// import Img10 from "../../Images/image21.png";
// import Img11 from "../../Images/image25.png";
// import Img12 from "../../Images/image35.png";
// import Img13 from "../../Images/image30.png";
// import Img16 from "../../Images/image43.png";
// import Img17 from "../../Images/image50.png";
// import Img18 from "../../Images/image55.png";
// import Img19 from "../../Images/chulu.png";
// import Img20 from "../../Images/lweendo.png";
// import Img21 from "../../Images/maureen.png";
// import Img22 from "../../Images/rosemary.png";
// import Img23 from "../../Images/sarah.png";
// import Img24 from "../../Images/sayari.png";
// import Img25 from "../../Images/sarahbanda.png";
// import Img26 from "../../Images/pedro.png";
// import Img27 from "../../Images/william.png";
// import Img28 from "../../Images/Kangwa.png";

// /* ————————————————— COMPONENTS ————————————————— */

// const TeamMemberCard = ({ member, onClick }) => (
//   <div className="team-member-card" onClick={() => onClick(member)}>
//     <div
//       className="member-image"
//       style={{
//         backgroundImage: member.image ? `url(${member.image})` : "none",
//         backgroundSize: "cover",
//         backgroundPosition: "center",
//       }}
//     />
//     <div className="member-info">
//       <h3 className="member-name">{member.name}</h3>
//       <p className="member-role">{member.role}</p>
//       <button className="learn-more-btn">
//         Read more <ArrowRight size={16} />
//       </button>
//     </div>
//   </div>
// );

// const MemberModal = ({ member, isOpen, onClose }) => {
//   if (!isOpen || !member) return null;

//   return (
//     <div className="modal-overlay" onClick={onClose}>
//       <div className="modal-content" onClick={(e) => e.stopPropagation()}>
//         <button className="modal-close" onClick={onClose}>
//           <X size={32} />
//         </button>

//         <div
//           className="modal-image"
//           style={{
//             backgroundImage: member.image ? `url(${member.image})` : "none",
//           }}
//         />

//         <div className="modal-info">
//           <h2>{member.name}</h2>
//           <h3 className="modal-role">{member.role}</h3>
//           <p className="modal-bio">{member.bio}</p>

//           <div className="modal-socials">
//             {member.social?.linkedin && (
//               <a
//                 href={member.social.linkedin}
//                 target="_blank"
//                 rel="noopener noreferrer"
//               >
//                 <Linkedin size={28} />
//               </a>
//             )}
//             {member.social?.twitter && (
//               <a
//                 href={member.social.twitter}
//                 target="_blank"
//                 rel="noopener noreferrer"
//               >
//                 <Twitter size={28} />
//               </a>
//             )}
//             {member.social?.email && (
//               <a href={`mailto:${member.social.email}`}>
//                 <Mail size={28} />
//               </a>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };



// /* BOARD */
// const boardOfDirectors = [
//   {
//     name: "Brian Mumba",
//     role: "Executive Chairperson & Associate Director Research and Development",
//     image: Img5,
//     bio: `Brian Mumba is a researcher and Educational Measurement and Behavioural Research specialist with over seven years of experience in monitoring, evaluation, and impact research across education, governance, gender, public health, climate change, and social development. He holds postgraduate qualifications and professional training in Educational Measurement and Evaluation, Applied Psychology, Programme Planning and Monitoring and Evaluation, and Corporate Governance, and is currently a PhD student in Educational Measurement and Evaluation. A Founder Member of DevLearn Impact Research Ltd, Brian provides strategic leadership to the organisation, with responsibility for governance oversight, quality assurance, partnerships, and institutional development. He serves as Board Chairperson and Chair of the Business Development and Strategy Committee, guiding DevLearn's long‑term growth, research innovation, and market positioning. Previously, Brian served as Acting Head of the Research Unit at the University of Lusaka, where he led research coordination and grant development. He has also held committee roles within the University of Lusaka and the Zambia Mining and Investment Insaka (ZAMII). In addition, he is a Part‑time Lecturer at the University of Lusaka, teaching advanced quantitative and qualitative data analysis.`,
//     social: {
//       linkedin: "https://www.linkedin.com/in/brianmumba/",
//     },
//   },
//   {
//     name: "Richard Mwale",
//     role: "Executive Vice Chairperson",
//     image: Img4,
//     bio: `Richard Mwale is a Mathematician and Data Analyst specialist with strong expertise in data analytics, financial modelling, and quantitative research methods. He holds a Bachelor of Science in Mathematics and Statistics, a Master of Science in Mathematical Sciences, and an MSc in Financial Engineering, strengthening his expertise in advanced analytics, risk modelling, and data-driven decision-making. A Founder Member of DevLearn Impact Research Ltd, Richard serves as Managing Director and Board Member, providing executive leadership across organisational operations, financial management, data systems, and project delivery. He is responsible for ensuring operational efficiency, financial integrity, and the timely delivery of high-quality research and consultancy outputs in line with ethical and professional standards. Beyond DevLearn, Mwale serves as a Lecturer and Actuarial Science Coordinator in the Department of Mathematics and Statistics at the University of Lusaka. He teaches advanced quantitative courses and coordinates academic programmes. His background combines strong analytical rigour with practical management experience, supporting DevLearn’s mission to deliver credible, data-driven insights for development policy and practice.`,
//     //bio: `Richard Mwale is a Mathematician and Applied Statistics specialist with strong expertise in data analytics, financial modelling, and quantitative research methods. He holds a Master of Science in Mathematical Sciences and also holds an MSc in Financial Engineering, strengthening his capacity in advanced analytics, risk modelling, and data‑driven decision‑making. A Founder Member of DevLearn Impact Research Ltd, Richard serves as Managing Director and Board Member, providing executive leadership across organisational operations, financial management, data systems, and project delivery. He is responsible for ensuring operational efficiency, financial integrity, and the timely delivery of high‑quality research and consultancy outputs in line with ethical and professional standards. Beyond DevLearn, Richard has held academic leadership roles as a Lecturer in Mathematics and Statistics at the University of Lusaka and Northrise University, where he has taught advanced quantitative courses and coordinated academic programmes. His background combines strong analytical rigor with practical management experience, supporting DevLearn's mission to deliver credible, data‑driven insights for development policy and practice.`,
//    // bio: `Richard Mwale is a Mathematician and Applied Statistics specialist with strong expertise in data analytics, financial modelling, and quantitative research methods. He holds a Master of Science in Mathematical Sciences and also holds an MSc in Financial Engineering, strengthening his capacity in advanced analytics, risk modelling, and data‑driven decision‑making. A Founder Member of DevLearn Impact Research Ltd, Richard serves as Managing Director and Board Member, providing executive leadership across organisational operations, financial management, data systems, and project delivery. He is responsible for ensuring operational efficiency, financial integrity, and the timely delivery of high‑quality research and consultancy outputs in line with ethical and professional standards. Beyond DevLearn, Richard has held academic leadership roles as a Lecturer in Mathematics and Statistics at the University of Lusaka and Northrise University, where he has taught advanced quantitative courses and coordinated academic programmes. His background combines strong analytical rigor with practical management experience, supporting DevLearn's mission to deliver credible, data‑driven insights for development policy and practice.`,
//     social: {
//       linkedin: "https://www.linkedin.com/in/richard-mwale",
//     },
//   },
// ];

// /* ADVISORS */
// const advisors = [
//   {
//     name: "Sarah Chisanga",
//     role: "Advisor to the Board – Policy, M&E & Inclusive Economic Development",
//     image: Img23,
//     bio: `Sarah Chisanga is a policy, research, and programme management specialist with over twelve years of experience supporting youth employment, entrepreneurship, and inclusive economic development across Zambia and East and Southern Africa. She holds a Master's degree in Business Administration and is pursuing a PhD in Business and Management. Her expertise spans policy and systems analysis, monitoring, evaluation, accountability, and learning (MEAL), evidence‑based advocacy, and multi‑stakeholder engagement. Sarah has held senior technical and leadership roles with international and regional institutions, including Expertise France, the United Nations World Food Programme, the ILO, UNHCR, Jhpiego, and national public‑sector institutions. Her work has contributed to policy formulation, programme adaptation, and inclusive economic empowerment strategies, with a strong focus on women, youth, and vulnerable populations. At DevLearn Impact Research Ltd, Sarah serves as an Advisor to the Board, providing strategic guidance on policy alignment, M&E, and inclusive economic development. She also serves as the Head of Strategy, Business Development & Partnership, leading the design and implementation of DevLearn's growth strategy, strengthening donor and partner relationships, guiding proposal positioning, and ensuring alignment between research excellence, market opportunities, and policy influence. Her expertise spans the design of robust M&E frameworks and the application of data‑driven approaches to impact assessment. Sarah is also a skilled trainer in research methodologies, statistical analysis, and data visualization, with a strong emphasis on adaptive management, learning, and sustainable development, including circular economy approaches.`,
//     social: {
//       linkedin: "https://www.linkedin.com/in/sarah-chisanga-88366a162/",
//     },
//   },
//   {
//     name: "Tracy Muleya Mbandama",
//     role: "Advisor to the Board – Governance, Ethics & Legal",
//     image: Img18,
//     bio: `Tracy Muleya Mbandama is a legal scholar and university lecturer with over seven years of experience in governance, human rights, constitutional law, and legal research. She holds multiple postgraduate qualifications in law, including a Master of Laws degree in Constitutional Law and Human Rights Law, and is currently pursuing a PhD in Law. She is also undertaking the Legal Practitioners Qualifying Examinations, towards admission to the Zambian Bar. Tracy has held academic and leadership roles within higher education, serving as a Lecturer in Law at Zambian Open University and as a Part‑time Lecturer at Copperbelt University. Her professional experience spans legal research, policy analysis, institutional governance, and public accountability, including participation in global research initiatives and national‑level policy dialogue. At DevLearn Impact Research Ltd, Tracy serves as an Advisor to the Board, providing independent guidance on governance integrity, ethics, safeguarding, and regulatory compliance. Her advisory role strengthens institutional accountability, risk management, and adherence to legal and ethical standards across the organisation's research and consultancy work.`,
//     social: {
//       linkedin: "https://www.linkedin.com/in/tracy-mbandama-0b386318b/",
//     },
//   },
//   {
//     name: "Hajara Amina Athman",
//     role: "Advisor to the Board – Education Systems & Capacity Building",
//     image: Img16,
//     bio: `Hajara Amina Athman is an Education Specialist and Curriculum Developer with over 15 years of experience in education reform, early‑grade literacy, teacher education, and capacity building. She has played a central role in Kenya's national curriculum reforms, contributing to the design and implementation of the Competency‑Based Curriculum (CBC) across early years, primary, secondary, and teacher education levels. Hajara has led and supported the development of national curriculum designs, assessment frameworks, and teaching and learning materials in English and indigenous languages, working closely with the Kenya Institute of Curriculum Development, the Kenya National Examinations Council, the Ministry of Education, and international partners. She has also served as a national master trainer and trainer of trainers, leading large‑scale teacher training and instructional support programmes under initiatives such as Tusome and the Kenya Primary Literacy Programme. At DevLearn Impact Research Ltd, Hajara serves as an Advisor to the Board, providing strategic guidance on strengthening education systems, learning quality, curriculum design, teacher professional development, and inclusive, systems‑level capacity‑building approaches.`,
//     social: {
//       linkedin: "https://www.linkedin.com/in/hajaraathman",
//     },
//   },
//   {
//     name: "Prof. Richard I. Mutemwa",
//     role: "Advisor to the Board – Scientific Oversight",
//     image: Img11,
//     bio: `Prof. Richard I. Mutemwa is a Public Health Researcher and Biostatistician with over twenty years of experience in research design, impact evaluation, and health systems strengthening across Africa and internationally. He holds a PhD in Public Health, a Master's degree in Medical Statistics, and is a Fellow of the Royal Statistical Society (United Kingdom). Prof. Mutemwa has held senior academic and technical leadership positions, including Professor and Head of Department at the University of Lusaka, Chief Scientific Officer at the Institute of Human Sciences (Zambia), and Adjunct Lecturer and Research Fellow at the London School of Hygiene & Tropical Medicine. He has led and advised large‑scale evaluations, baseline studies, and policy‑relevant research for governments, UN agencies, bilateral donors, and international NGOs across health, nutrition, HIV, and social development sectors. At DevLearn Impact Research Ltd, Prof. Mutemwa serves as an Advisor to the Board, providing independent scientific oversight on research quality, ethics, methodological rigor, data integrity, and evaluation standards, ensuring that the organisation's work meets internationally recognised research and ethical benchmarks.`,
//     social: {},
//   },
// ];

// /* LEADERSHIP & MANAGEMENT */
// const leadership = [
//   {
//     name: "Richard Mwale",
//     role: "Managing Director",
//     image: Img4,
//     bio: `Richard Mwale holds a BSc in Mathematics from the Copperbelt University, Kitwe, Zambia, and an MSc in Mathematical Science, specialising in Financial Mathematics, from Stellenbosch University, Cape Town, South Africa. He also holds an MSc in Financial Engineering, with a focus on advanced statistical modelling, optimisation, and data-driven decision-making. Richard has over five years of experience in research, consulting, and teaching, with expertise in applied statistics, operations research, statistical inference, and data analytics. His technical skills include predictive modelling, risk modelling, time series forecasting, and the integration of machine learning with classical inference methods. He is proficient in Python, R, Stata, SAS, SPSS, Microsoft Office, and LaTeX, and has working knowledge of MATLAB, which he applies extensively in statistical analysis, risk modelling, and decision support systems across diverse sectors, including economics, finance, health, education, and climate change.  As Managing Director of DevLearn Impact Research Ltd, Mwale provides overall leadership and strategic direction, ensuring that the organisation’s operations, programmes, and financial strategies align with its mission. His leadership emphasises measurable impact and accountability, and positions DevLearn as a trusted and innovative research partner. `,
//     social: {
//       linkedin: "https://www.linkedin.com/in/richard-mwale",
//     },
//   },
//   {
//     name: "Maureen Kamalizeni",
//     role: "Associate – Strategy, Grants & International Partnerships",
//     image: Img21,
//     //bio: `Maureen is an Economist by profession with over four years of experience in the field. She holds an MSc in Economics and Policy Analysis with First Class Honours from the University of Limerick and a bachelor's degree in economics from the University of Malawi. She has a strong interest in applying economic analysis to inform practical, inclusive policy decisions, particularly in public policy, climate change, agriculture, and development. As the Associate for Strategy, Grants & International Partnerships at DevLearn Impact Research Ltd, she contributes to strategic planning and supports grant and partnership development. Her work involves providing analytical inputs to policy and economic sections, theories of change, results frameworks, MEL logic, and value‑for‑money narratives. She also contributes to concept notes and proposals and identifies funding opportunities. Maureen's strengths lie in research design, data analysis, and policy‑oriented writing.`,
//     bio: `Maureen is an Economist by profession with over four years of experience in the field. She holds a Master's degree in Economics and Policy Analysis with First Class Honours from the University of Limerick, and a Bachelor's degree in Economics with credit from the University of Malawi. She has a strong interest in applying economic analysis to inform practical policy decisions particularly in public policy, climate change, agriculture, and development. As the Associate for Strategy, Grants & International Partnerships at DevLearn Impact Research Ltd, she contributes to strategic planning and supports grant and partnership development. Her work involves providing analytical inputs to policy and economic sections, theories of change, results frameworks, MEL logic, and value‑for‑money narratives. She also contributes to concept notes and proposals and identifies funding opportunities. Maureen's strengths lie in research design, data analysis, and policy‑oriented writing.`,
//     social: {
//       linkedin: "https://www.linkedin.com/in/maureen-kamalizeni-11975a272",
//     },
//   },
//   {
//     name: "Erick Dalitso Mwansa",
//     role: "Business Development and Partnerships Manager",
//     image: Img8,
//     bio: `Erick Dalitso Mwansa holds a Bachelor of Arts in Sociology and Development Studies and a Certificate in Monitoring and Evaluation and is currently pursuing a Master of Science in Public Health (final stage) at the University of Zambia. A Registered Health Researcher with the National Health Research Authority, he has over 5 years of experience managing donor‑funded programmes in health, education, WASH, nutrition, climate resilience, and HIV/AIDS across Zambia, Ghana, Uganda, and Denmark, working with USAID, PEPFAR, Comic Relief, SIDA, CISU, UNICEF, and the EU. At DevLearn Impact Research Ltd, he serves as a Business Development and Partnerships Manager, leading business development initiatives, proposal development, and partnership cultivation. Proficient in Microsoft Project, SPSS, DHIS2, Stata, Power BI, and KoboToolbox, he integrates academic rigour with field experience to ensure that research informs programmes and policy.`,
//     social: {
//       linkedin: "https://www.linkedin.com/in/erick-dalitso-mwansa-9554411a5",
//     },
//   },
//   {
//     name: "Benjamin Lungu",
//     role: "Knowledge Translation & Communication Associate",
//     image: Img7,
//     bio: `Benjamin Lungu is a communication and marketing specialist with expertise in digital branding, creative design, and knowledge translation. He holds a BA in Library and Information Science & Psychology, as well as a Diploma in Marketing Management. At DevLearn, he supports knowledge translation and communication, drawing on over three years of experience from Coca‑Cola Beverages, Zambia Sugar, and Engen Zambia. He also holds certifications in digital marketing, project management, media and entertainment (M&E), and journalism.`,
//     social: {},
//   },
//   {
//     name: "Lweendo Muyola",
//     role: "Research Associate – Evaluations & Field Operations",
//     image: Img20,
//     bio: `Lweendo Muyola holds a Master of Business Administration (MBA) in Project Management from Dr. D. Y. Patil Vidyapeeth, Pune (DPU) and a Bachelor of Science in Wood Science and Technology from the Copperbelt University. He combines strong technical research competencies with practical experience in field leadership, coordination, and reporting, making him a valuable asset to evaluation and impact research teams. Lweendo has extensive experience supporting evaluations, field operations, and data collection across the agriculture, health, education, sanitation, livelihoods, and social development sectors in Zambia. As a Research Associate – Evaluations & Field Operations at DevLearn Impact Research Ltd, he supports the planning and execution of evaluations, coordinates and supervises field teams, and ensures high‑quality data collection through robust quality assurance and control processes. He leads and co‑leads field‑based research activities, including baseline, midline, end‑line, and evaluation studies, and supports enumerator recruitment, training, supervision, and quality control. He is highly skilled in digital data collection and field operations, with hands‑on experience with KoboToolbox, ODK, Survey Solutions, GeoQuest, and FieldTask, as well as GPS field mapping and data quality assurance systems. Lweendo is fluent in English, Tonga, Nyanja, and Bemba, enabling effective community engagement and stakeholder coordination across diverse contexts.`,
//     social: {
//       linkedin: "https://www.linkedin.com/in/lweendo-muyola-751aa079/",
//     },
//   },
//   {
//     name: "Dorcas Kalaba",
//     role: "Research Associate",
//     image: Img6,
//     bio: `Dorcas is a master's student in Epidemiology and Biostatistics with practical experience in research design, data analysis, and scientific writing. A licensed pharmacist with over three years of professional experience in industrial, hospital, and retail pharmacy settings, she brings strong analytical, problem‑solving, and leadership skills to her work. As a Research Associate at Devlearn Impact Research Ltd, she contributes to study design, data collection, analysis, and technical proposal writing, among other tasks.`,
//     social: {
//       linkedin: "https://www.linkedin.com/in/kalabadorcas/",
//     },
//   },
//    {
//     name: "Chilombo Mwambazi",
//     role: "Research Associate",
//     image: Img9,
//     bio: `Chilombo Mwambazi is a Research Associate and public health professional with a Master of Science in Public Health from the University of Zambia and over 12 years of experience in research, youth development, and Programme management. She has contributed to studies in governance, clinical trials, RMNCAH, HIV/AIDS, and adolescent health, with expertise in study design, data collection, analysis, and policy engagement. At DevLearn Impact Research Ltd, she supports proposal development, research design, field coordination, data management, and reporting, contributing to publications and policy briefs. Proficient in NVivo, SPSS, and Kobo Toolbox, she integrates academic rigour with field experience to ensure research informs programmes and policy.`,
//     social: {
//       linkedin: "https://www.linkedin.com/in/chilombo-mwambazi-b73973229",
//     },
//   },
//   {
//     name: "Khaoula Sayari",
//     role: "Research Associate",
//     image: Img24,
//     bio: `Khaoula Sayari is a biotechnology professional holding a bachelor's degree in biotechnology and Entrepreneurship from the Higher Institute of Biotechnology of Monastir. She is currently pursuing a master's degree in molecular Genetics at Manisa Celal Bayar University, further strengthening her expertise in advanced biological sciences and research methodologies. In parallel with her academic training, Khaoula has solid experience in sales management and marketing. She previously served as Sales Manager at Yuto Türkiye, where she led sales management, developed partnership strategies, and managed stakeholder engagement. She also holds marketing and communication certifications, enabling her to contribute effectively to business development, outreach, and project growth. As a Research Associate at DevLearn Impact Research Ltd, she provides translation and multilingual support (e.g., English, French, Arabic, and other relevant languages) for: Research tools and instruments, Proposals and concept notes, and Reports, briefs, and communication materials. She is fluent in Arabic, English, French, and Turkish.`,
//     social: {
//       linkedin: "https://www.linkedin.com/in/khaoula-sayari-131ba5213/?locale=en_US",
//     },
//   },
//   {
//     name: "Rosemary Moonga",
//     role: "Administrative Assistant Intern",
//     image: Img22,
//     bio: `Rosemary Moonga is a third‑year student in Rural Sociology and Community Development, with a minor in Psychology, and has completed short courses in Project Management and Planning, Digital Marketing, and Virtual Administration. As an Administrative Assistant Intern at DevLearn Impact Research Ltd., she provides administrative, coordination, and communications support to management and project teams across the organisation's research, training, and consultancy activities. Rosemary assists with communications and outreach by posting approved social media updates and opportunities and responding to basic enquiries under guidance. She also supports the coordination of short courses and training programmes, including managing registrations, participant enquiries, and virtual training logistics.`,
//     social: {
//       linkedin: "https://www.linkedin.com/in/rosemary-malala",
//     },
//   },
    
// ];

// /* SENIOR & ASSOCIATE CONSULTANTS */
// const seniorAssociates = [
//   {
//     name: "Zangose Tembo Lumbwe",
//     role: "Senior Associate Consultant",
//     image: Img12,
//     bio: `Zangose holds a Master's in International Development (Warwick) and a BA in International Relations (Leicester). She is an accomplished gender and social inclusion specialist with over 10 years of experience in international development, stakeholder engagement, and policy advocacy across Zambia and the UK. She has served as a Gender Consultant to the Ministry of Energy (Zambia), where she supports the operationalisation of the EU‑funded Gender Equality Strategy and Action Plan for the Energy Sector through advisory services, workplan reviews, and multi‑stakeholder coordination. Previously, she worked with USAID, Tetra Tech, GIZ, and the World Bank, leading gender and social inclusion research, action plan development, and capacity building under programmes including ZMELP, A2C Clean Cooking, ZEWASH, and the Zambia Agribusiness and Trade Project. Zangose has also trained frontline service providers on gender‑based violence response, mentored gender officers, and led national‑level gender analyses and youth engagement strategies. She is widely published on gender, youth, and governance, and is a recognised advocate for inclusive and sustainable development.`,
//     social: {
//       linkedin: "https://www.linkedin.com/in/zangose-tembo-lumbwe-b68004122/",
//     },
//   },
//   {
//     name: "Dr. Abigail Moono Mukuwa Tuchili",
//     role: "Senior Associate Consultant",
//     image: Img13,
//     bio: `Dr. Abigail Mukuwa Tuchili is an academic, consultant, and education leader specialising in curriculum development, educational leadership, and learner‑centred pedagogy. She has advised universities, schools, and community initiatives, with a strong focus on inclusive and equitable education. Dr. Tuchili serves on the Nursing and Midwifery Council of Zambia as a Board Member and Chair of the Examinations Committee. Additionally, from 2025 to 2028, she will serve on the Zambia Conference of Catholic Bishops' Committee on Education and Culture. An author and speaker, she has published widely and co‑authored textbooks that promote literacy, creativity, and critical thinking.`,
//     social: {},
//   },
//   {
//     name: "Sarah Banda",
//     role: "Senior Associate Consultant",
//     image: Img25,
//     bio: `Sarah Banda is a senior social protection and disability inclusion specialist with over 19 years of experience leading government and donor‑funded programmes across Zambia, with a strong focus on women, children, and persons with disabilities. She previously served as Provincial Social Welfare Officer (Lusaka) and Senior Social Welfare Officer (National Level) at the Ministry of Community Development and Social Services, where she led the national implementation of the Public Welfare Assistance Scheme (PWAS) and coordinated disability mainstreaming as the National Disability Focal Point Person. Sarah played a key role in national systems reform, including the enactment of the Persons with Disabilities Act (2012), rollout of the National Disability Policy (2015), Zambia's UNCRPD State Party Report, and coordination of the 2015 National Disability Survey with MCDSS, UNICEF, and the University of Zambia. She also led education initiatives under the USAID/UNICEF SEEVCA programme, reaching over 14,000 vulnerable children and adolescents. She is currently Principal Research Scientist at University Teaching Hospitals – Adult.`,
//     social: {},
//   },
//   {
//     name: "Dr. Pedro João",
//     role: "Senior Associate Consultant",
//     image: Img26,
//     bio: `Dr Pedro João Uetela is a Mozambican scholar and lecturer specialising in higher education policy, development, and sociology. He holds a PhD in Social Sciences and has completed postdoctoral fellowships at the University of Ghana and the University of Western Cape. With over a decade of experience in teaching, research, and leadership, he has directed research centres, coordinated international academic programs, and authored books and articles on higher education, decolonisation, and socio‑economic development in Africa. Fluent in four languages, Dr. Uetela brings global expertise and local insight to advancing inclusive education and policy innovation.`,
//     social: {
//       linkedin: "https://www.linkedin.com/in/pedro-uetela-65393296/",
//     },
//   },
//   {
//     name: "Elizabeth Mwansa",
//     role: "Associate Consultant",
//     image: Img10,
//     bio: `Elizabeth Mwansa is a Safeguarding and Gender professional with over 10 years of experience in the humanitarian and development sectors, including education, health, agriculture, livelihoods, and disaster response. She holds a Master's in Peace, Leadership, and Conflict Resolution, a Bachelor of Social Work, and is pursuing a Law degree. Currently, she serves as the Regional Safeguarding and Social Inclusion Coordinator at World Renew, where she has led the development of safeguarding frameworks, gender justice initiatives, and GBV structures across Southern Africa. Recognised for strengthening partner organisations, she provides technical support, training, and policy guidance to promote safeguarding, gender equality, and social inclusion.`,
//     social: {},
//   },

 
//   {
//     name: "Chikondi Mkonda Mvula",
//     role: "Associate Consultant",
//     image: Img17,
//     bio: `Chikondi Mkonda Mvula holds a Master's degree in Project Management and a Bachelor's degree in Development Studies. She is a seasoned public health professional with over nine years of experience leading public health interventions, including malaria prevention and sanitation and hygiene initiatives in Zambia. She brings deep expertise in participatory data collection, social accountability mechanisms, and multi‑stakeholder engagement to strengthen WASH service delivery and health governance.`,
//     social: {
//       linkedin: "https://www.linkedin.com/in/chikondi-mkonda",
//     },
//   },
//   {
//     name: "Musadabwe Chulu",
//     role: "Associate Consultant",
//     image: Img19,
//     bio: `Musadabwe Chulu is a Public Policy Analyst and Agricultural Economist with 18 years of experience in agricultural policy, labour market policies, and development programme design. He holds a Bachelor of Agricultural Science (Agricultural Economics) and a Master of Management Science in Public Policy from Peking University and has worked with international NGOs and governments. His expertise spans agricultural public policy linkages, social protection, and nutrition, with a proven track record in policy analysis and program implementation. Currently affiliated with the Ministry of Finance and National Planning and the University of Lusaka, Musadabwe is recognised for advancing evidence‑based policies that strengthen agriculture, employment, and social protection systems.`,
//     social: {
//       linkedin: "https://www.linkedin.com/in/musadabwe-chulu-2ba18a3a/",
//     },
//   },
//       {
//       name: 'Engineer Dr. William Mutonga',
//       role: 'Senior Associate Consultant',
//       bio: 'PhD in Entrepreneurship, MBA in Finance, BSc in Chemical Engineering. Over 15 years in Zambia’s extractives, energy, and environmental sectors; expertise in resource governance and policy.',
//       image: Img27,
//       social: { linkedin: '#', twitter: '#', email: 'william@devlearnimpact.com' }
//     },
//       {
//         name: "Kangwa Kasonde",
//         role: "Associate Consultant",
//         image: Img28,
//         bio: `Kangwa is a dedicated Adolescent Health Specialist with over 16 years of extensive experience in Social Work, Mental Health, Special Education, and Sexual and Reproductive Health (SRH). She has demonstrated expertise in HIV prevention among youth, especially Adolescent Girls and Young Women (AGYW), through leadership roles such as her involvement in the DREAMS Initiative and her current position as District Coordinator supporting 20 health facilities under a UNICEF-funded HIV prevention program. Awarded as USAID CHEKUP II Most innovative staff 2024, Kangwa excels in strengthening adolescent health networks, community engagement, and partnership building to drive sustainable change. Her scholarly contributions include abstracts accepted at IAS 2024 and 2025 (Scholarship recipient), HIV & Women 2024 & 2025, INTEREST 2024 & 2025, Adolescence, SRHR & HIV 2023-2025, and ICASA 2023, where she has served as a panelist, establishing her as a regional and global HIV thought leader. As a certified Families! Matter Global Trainer, she has delivered behavioural change communication and capacity building trainings across multiple countries, promoting youth-cantered and participatory approaches. Her innovative methods and mentorship in research and program development make her an ideal candidate for senior advisory roles. Kangwa is committed to advancing adolescent health, SRHR, and community-based interventions through strategic, evidence-based, and collaborative efforts.`,
//         social: {
//           linkedin: "https://www.linkedin.com/in/kangwa-kasonde-b422aa140/",
//         },
//       },
// ];

// /* ————————————————— TEAM PAGE ————————————————— */

// const Team = () => {
//   const [selectedMember, setSelectedMember] = useState(null);

//   return (
//     <>
//       {/* Hero */}
//            {/* Hero */}
//        <section className="hero">
//          <div className="container text-center">
//            <h1>Our Team</h1>
//            <p style={{color : '#000000'}}>
//              A multidisciplinary team of researchers, data scientists, MERL experts, and consultants committed to local ownership, evidence-based practice, and inclusive development in Zambia and beyond.
//            </p>
//            <div className="hero-buttons">
//              <a href="#board" className="btn btn-primary">
//                Meet Our Leadership<ArrowRight />
//              </a>
//              <a href="#seniors" className="btn btn-secondary">
//                Thematic Experts
//              </a>
//               <a href="#leadership" className="btn btn-primary">
//                Management <ArrowRight />
//              </a>
//            </div>
//          </div>
//        </section>
  

//       {/* Board */}
//       <section id="board" className="section">
//         <div className="container text-center">
//           <h2>Board of Directors</h2>
//           <div className="team-grid modern-grid">
//             {boardOfDirectors.map((member, i) => (
//               <TeamMemberCard key={i} member={member} onClick={setSelectedMember} />
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Advisors */}
//       <section id="advisors" className="section bg-light">
//         <div className="container text-center">
//           <h2>Advisors to the Board</h2>
//           <div className="team-grid modern-grid">
//             {advisors.map((member, i) => (
//               <TeamMemberCard key={i} member={member} onClick={setSelectedMember} />
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Leadership */}
//       <section id="leadership" className="section">
//         <div className="container text-center">
//           <h2>Our Management Team</h2>
//           <div className="team-grid modern-grid">
//             {leadership.map((member, i) => (
//               <TeamMemberCard key={i} member={member} onClick={setSelectedMember} />
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Associates */}
//       <section id="seniors" className="section bg-light">
//         <div className="container text-center">
//           <h2>Thematic Experts</h2>
//           <div className="team-grid modern-grid">
//             {seniorAssociates.map((member, i) => (
//               <TeamMemberCard key={i} member={member} onClick={setSelectedMember} />
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* CTA */}
//       <section className="final-cta text-center">
//         <div className="container">
//           <h2>Inspired by Our Team?<br />Let's Collaborate on Impactful Research.</h2>
//           <a href="/contact" className="btn btn-primary">
//             Get in Touch <ArrowRight size={28} />
//           </a>
//         </div>
//       </section>

//       {/* Modal */}
//       <MemberModal
//         member={selectedMember}
//         isOpen={!!selectedMember}
//         onClose={() => setSelectedMember(null)}
//       />

//       {/* Footer */}
//       <footer className="footer">
//         <div className="footer-grid container">
//           <div>
//             <div className="footer-logo">DevLearn Impact Research Ltd</div>
//             <p>Plot No. 3788/A, Avondale Road, Lusaka</p>
//             <p>+260 973 902 981 / +260 978 341 825</p>
//             <p>info@devlearnimpact.com</p>
//           </div>
//           <div>
//             <h4>Services</h4>
//             <ul>
//               <li><a href="/services">Applied Research</a></li>
//               <li><a href="/services">MERL Frameworks</a></li>
//               <li><a href="/services">Capacity Building</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Thematic Areas</h4>
//             <ul>
//               <li><a href="/sectors">Education & Health</a></li>
//               <li><a href="/sectors">Governance & Climate</a></li>
//               <li><a href="/sectors">Economic Inclusion</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Company</h4>
//             <ul>
//               <li><a href="/about">About</a></li>
//               <li><a href="/team">Team</a></li>
//               <li><a href="/case-studies">Projects</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Connect</h4>
//             <ul>
//               <li><a href="https://www.linkedin.com/company/devlearn-impact-research/">LinkedIn</a></li>
//               <li><a href="https://www.facebook.com/share/1AS3Ahfsy7/">Facebook</a></li>
//             </ul>
//           </div>
//         </div>
//         <div className="copyright">
//           © 2026 DevLearn Impact Research Ltd. All rights reserved.
//         </div>
//       </footer>
//     </>
//   );
// };

// export default Team;

//ENDS HERE

// // pages/Team/Team.jsx
// import { useState } from 'react';
// import { ArrowRight, Linkedin, Twitter, Mail, X } from 'lucide-react';
// import '../../index.css'; // global palette and variables
// import './Team.css'; // page-specific styles

// import Img4 from "../../Images/image4.png";
// import Img5 from "../../Images/image8.png";
// import Img6 from "../../Images/image12.png"; //dorcas
// import Img7 from "../../Images/image63.png"; //benjamin
// import Img8 from "../../Images/image60.png"; //benjamin
// import Img9 from "../../Images/image16.png"; //chilibombo
// import Img10 from "../../Images/image21.png"; //elizabeth
// import Img11 from "../../Images/image25.png"; //prof richard
// import Img12 from "../../Images/image35.png"; //zangose
// import Img13 from "../../Images/image30.png"; //abigail
// import Img14 from "../../Images/image40.png"; //silungwe
// // import Img15 from "../../Images/image43.png"; //hajara
// import Img16 from "../../Images/image43.png"; //hajara
// import Img17 from "../../Images/image50.png"; //chikondi
// import Img18 from "../../Images/image55.png"; //tracy




// const TeamMemberCard = ({ member, onClick }) => (
//   <div
//     className="team-member-card"
//     role="button"
//     tabIndex={0}
//     onKeyDown={(e) => e.key === 'Enter' && onClick(member)}
//   >
//     <div
//       className="member-image"
//       style={{
//         backgroundImage: member.image ? `url(${member.image})` : 'none',
//         backgroundSize: 'cover',
//         backgroundPosition: 'center'
//       }}
//     />
//     <div className="member-info">
//       <h3 className="member-name">{member.name}</h3>
//       <p className="member-role">{member.role}</p>
//       <button className="learn-more-btn" onClick={() => onClick(member)}>
//         Read more <ArrowRight size={16} />
//       </button>
//     </div>
//   </div>
// );

// const MemberModal = ({ member, isOpen, onClose }) => {
//   if (!isOpen || !member) return null;

//   return (
//     <div className="modal-overlay" onClick={onClose}>
//       <div className="modal-content" onClick={e => e.stopPropagation()}>
//         <button className="modal-close" onClick={onClose}>
//           <X size={32} />
//         </button>

//         <div
//           className="modal-image"
//           style={{
//             backgroundImage: member.image ? `url(${member.image})` : 'none'
//           }}
//         />

//         <div className="modal-info">
//           <h2>{member.name}</h2>
//           <h3 className="modal-role">{member.role}</h3>
//           <p className="modal-bio">{member.bio}</p>

//           <div className="modal-socials">
//             {member.social?.linkedin && member.social.linkedin !== '#' && (
//               <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
//                 <Linkedin size={28} />
//               </a>
//             )}
//             {member.social?.twitter && member.social.twitter !== '#' && (
//               <a href={member.social.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter">
//                 <Twitter size={28} />
//               </a>
//             )}
//             {member.social?.email && (
//               <a href={`mailto:${member.social.email}`} aria-label="Email">
//                 <Mail size={28} />
//               </a>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// const Team = () => {
//   const [selectedMember, setSelectedMember] = useState(null);

//   const leadership = [
//     {
//       name: 'Richard Mwale',
//       role: 'Managing Director',
//       bio: 'BSc in Mathematics (Copperbelt University), MSc in Mathematical Sciences (Stellenbosch University). Over 5 years in research, consultancy, and teaching; specializing in applied statistics, data analytics, and predictive modeling. Provides strategic leadership for measurable impact.',
//       //image: '/images/team/richard-mwale.jpg', // ← Update with real image paths
//       image: Img4,
//       social: { linkedin: 'https://www.linkedin.com/in/richard-mwale', twitter: '#', email: 'richard@devlearnimpact.com' }
//     },
//     {
//       name: 'Brian Mumba',
//       role: 'Head of Research and Development',
//       bio: 'MSc in Educational Measurement and Evaluation; pursuing PhD and MA in Psychology. Over 7 years in research design, data analysis, and program management. Leads research strategy, evaluations, and capacity building.',
//       image: Img5,
//       social: { linkedin: 'https://www.linkedin.com/in/brianmumba/', twitter: '#', email: 'brian@devlearnimpact.com' }
//     },
//     {
//       name: 'Erick Dalitso Mwansa',
//       role: 'Business Development & Partnerships Manager',
//       bio: 'Bachelor of Laws (University of Lusaka). Experience in legal and corporate sectors; passionate about youth mentorship. Oversees executive support, governance, compliance, and organizational efficiency.',
//       image: Img8,
//       social: { linkedin: 'https://www.linkedin.com/in/juliet-phiri-glm365diay', twitter: '#', email: 'juliet@devlearnimpact.com' }
//     },
  
      
//     // {
//     //   name: 'Ndong’a Kamelu',
//     //   role: 'Research Associate',
//     //   bio: 'Economist and policy analyst with MSc in Economics and MBA in Finance. Over 10 years in research, strategy, and program evaluation; supports design, proposals, capacity building, and policy briefs.',
//     //   image: '/images/team/ndonga-kamelu.jpg',
//     //   social: { linkedin: 'https://www.linkedin.com/in/joshua-ndong-a-kamelu-9a20a71b7/', twitter: '#', email: 'ndonga@devlearnimpact.com' }
//     // },
//     {
//       name: 'Benjamin Lungu',
//       role: 'Knowledge Translation and Communication Consultant',
//       bio: 'BA in Library and Information Science & Psychology; Diploma in Marketing Management. Over 10 years in media production, creative design, and knowledge translation; supports communication and branding.',
//       image: Img7,
//       social: { linkedin: 'https://www.linkedin.com/in/benjamin-lungu-9447961b4', twitter: '#', email: 'benjamin@devlearnimpact.com' }
//     }
//   ];

//   const seniorAssociates = [
//     {
//       name: 'Professor Richard Mutemwa',
//       role: 'Senior Associate Consultant',
//       bio: 'PhD in Public Health, MSc in Medical Statistics, MBA in Public Health. Over 20 years in academia and consultancy; specializes in health systems, policy research, and statistical analysis.',
//       image: Img11,
//       social: { linkedin: '#', twitter: '#', email: 'richard.mutemwa@devlearnimpact.com' }
//     },
    // {
    //   name: 'Engineer Dr. William Mutonga',
    //   role: 'Senior Associate Consultant',
    //   bio: 'PhD in Entrepreneurship, MBA in Finance, BSc in Chemical Engineering. Over 15 years in Zambia’s extractives, energy, and environmental sectors; expertise in resource governance and policy.',
    //   image: '/images/team/william-mutonga.jpg',
    //   social: { linkedin: '#', twitter: '#', email: 'william@devlearnimpact.com' }
    // },
//     {
//       name: 'Zangose Tembo Lumbwe',
//       role: 'Senior Associate Consultant',
//       bio: 'Master’s in International Development, Bachelor’s in International Relations. Over 10 years in gender equality, agriculture, food security, and climate resilience; supports inclusive initiatives.',
//       image: Img12,
//       social: { linkedin: 'https://www.linkedin.com/in/zangose-tembo-lumbwe-b68004122/', twitter: '#', email: 'zangose@devlearnimpact.com' }
//     },
//     {
//       name: 'Dr. Abigail Mukuwa Tuchili',
//       role: 'Senior Associate Consultant',
//       bio: 'Education leader specializing in curriculum development, learner-centered pedagogy, and inclusive education. Advises universities and schools; serves on key committees in nursing and education.',
//       image: Img13,
//       social: { linkedin: '#', twitter: '#', email: 'abigail@devlearnimpact.com' }
//     },
//     {
//       name: 'Dr. Wilson Silungwe',
//       role: 'Senior Associate Consultant',
//       bio: 'Communication and education expert with over 18 years in academia and consultancy. Pursuing PhD in Communication; supports strategy, policy analysis, and institutional capacity development.',
//       image: Img14,
//       social: { linkedin: 'https://www.linkedin.com/in/dr-wilson-silungwe-53a476254/', twitter: '#', email: 'wilson@devlearnimpact.com' }
//     },
//     // {
//     //   name: 'Dr. Pedro João Uetela',
//     //   role: 'Senior Associate Consultant',
//     //   bio: 'PhD in Social Sciences; postdoctoral fellowships in higher education policy. Over a decade in development sociology; advances inclusive education and policy innovation.',
//     //   image: '/images/team/pedro-joao-uetela.jpg',
//     //   social: { linkedin: 'https://www.linkedin.com/in/pedro-uetela-65393296/', twitter: '#', email: 'pedro@devlearnimpact.com' }
//     // },
//     {
//       name: 'Hajara Amina Athman',
//       role: 'Senior Associate Consultant',
//       bio: 'Master’s in Linguistics. Expertise in education systems, curriculum development, teacher training, and early-grade literacy; supports language and education reform.',
//       image: Img16,
//       social: { linkedin: 'https://www.linkedin.com/in/hajaraathman', twitter: '#', email: 'hajara@devlearnimpact.com' }
//     },
//     {
//       name: 'Elizabeth Mwansa',
//       role: 'Associate Consultant',
//       bio: 'Safeguarding and gender professional with over 10 years in education, health, agriculture, and disaster response. Master’s in Peace, Leadership and Conflict Resolution; supports gender justice and inclusion.',
//       image: Img10,
//       social: { linkedin: 'https://www.linkedin.com/in/elizabeth-mwansa-627a43100/', twitter: '#', email: 'elizabeth@devlearnimpact.com' }
//     },
//       {
//       name: 'Dorcas Kalaba',
//       role: 'Research Associate',
//       bio: 'Licensed pharmacist; Master’s student in Epidemiology and Biostatistics. Expertise in research design, data analysis, and scientific writing. Supports study design, data collection, analysis, and proposals.',
//       image: Img6,
//       social: { linkedin: 'https://www.linkedin.com/in/kalabadorcas/', twitter: '#', email: 'dorcas@devlearnimpact.com' }
//     },
//     {
//       name: 'Chilombo Mwambazi',
//       role: 'Research Associate',
//       bio: 'MSc in Public Health (University of Zambia). Over 12 years in research, youth development, and program management; expertise in governance, clinical trials, RMNCAH, HIV/AIDS, and adolescent health.',
//       image: Img9,
//       social: { linkedin: 'https://www.linkedin.com/in/chilombo-mwambazi-b73973229', twitter: '#', email: 'chilombo@devlearnimpact.com' }
//     },
//     // {
//     //   name: 'Musadabwe Chulu',
//     //   role: 'Associate Consultant',
//     //   bio: 'Public Policy Analyst and Agricultural Economist with 18 years in policy, labor markets, and development programs. Master’s in Public Policy; advances evidence-based agriculture and social protection.',
//     //   image: '/images/team/musadabwe-chulu.jpg',
//     //   social: { linkedin: 'https://www.linkedin.com/in/musadabwe-chulu-2ba18a3a/', twitter: '#', email: 'musadabwe@devlearnimpact.com' }
//     // },
//     {
//       name: 'Chikondi Mkonda Mvula',
//       role: 'Associate Consultant',
//       bio: 'Master’s in Project Management; over 9 years in public health and WASH. Leads malaria, sanitation, and hygiene programs with participatory data and social accountability.',
//       image: Img17,
//       social: { linkedin: 'https://www.linkedin.com/in/chikondi-mkonda', twitter: '#', email: 'chikondi@devlearnimpact.com' }
//     },
//     // {
//     //   name: 'Nachizya Edith Namukanga',
//     //   role: 'Associate Consultant',
//     //   bio: 'Master’s in Public Health (Health Promotion); pursuing PhD in Public Health Epidemiology. Focus on adolescent health and menstrual management; supports health policy and program design.',
//     //   image: '/images/team/nachizya-namukanga.jpg',
//     //   social: { linkedin: 'https://www.linkedin.com/in/nachizyanamukanga', twitter: '#', email: 'nachizya@devlearnimpact.com' }
//     // },
//     // {
//     //   name: 'Kavuyi Kamelu-Soko',
//     //   role: 'Associate Consultant',
//     //   bio: 'Expertise in law, gender, and project management. Lecturer and Coordinator at University of Lusaka; leads EU-funded projects for inclusive, gender-responsive development.',
//     //   image: '/images/team/kavuyi-kamelu-soko.jpg',
//     //   social: { linkedin: '#', twitter: '#', email: 'kavuyi@devlearnimpact.com' }
//     // },
//     {
//       name: 'Tracy Muleya Mbandama',
//       role: 'Associate Consultant',
//       bio: 'Legal scholar in constitutional and human rights law, climate justice, and governance. Pursuing PhD in Law; supports rights-based advocacy and policy development.',
//       image: Img18,
//       social: { linkedin: 'https://www.linkedin.com/in/tracy-mbandama-0b386318b/', twitter: '#', email: 'tracy@devlearnimpact.com' }
//     },
//     // {
//     //   name: 'Brian Chonya',
//     //   role: 'Associate Consultant',
//     //   bio: 'Chartered Global Management Accountant with 20+ years in financial management and project accounting. Supports donor-funded projects for UN, USAID, and World Bank.',
//     //   image: '/images/team/brian-chonya.jpg',
//     //   social: { linkedin: 'https://www.linkedin.com/in/chonyabriancgma/', twitter: '#', email: 'brian.chonya@devlearnimpact.com' }
//     // },
//     // {
//     //   name: 'Modesta Phiri',
//     //   role: 'Associate Consultant',
//     //   bio: 'Social Protection Specialist with 10+ years in child and social protection programs. MSc in Project Management; advances gender equality and child rights at Ministry level.',
//     //   image: '/images/team/modesta-phiri.jpg',
//     //   social: { linkedin: 'https://www.linkedin.com/in/modesta-phiri-06a70064/', twitter: '#', email: 'modesta@devlearnimpact.com' }
//     // }
//   ];

//   // const associateConsultants = [

//   //    {
//   //     name: 'Elizabeth Mwansa',
//   //     role: 'Associate Consultant',
//   //     bio: 'Safeguarding and gender professional with over 10 years in education, health, agriculture, and disaster response. Master’s in Peace, Leadership and Conflict Resolution; supports gender justice and inclusion.',
//   //     image: Img10,
//   //     social: { linkedin: 'https://www.linkedin.com/in/elizabeth-mwansa-627a43100/', twitter: '#', email: 'elizabeth@devlearnimpact.com' }
//   //   },
//   //     {
//   //     name: 'Dorcas Kalaba',
//   //     role: 'Research Associate',
//   //     bio: 'Licensed pharmacist; Master’s student in Epidemiology and Biostatistics. Expertise in research design, data analysis, and scientific writing. Supports study design, data collection, analysis, and proposals.',
//   //     image: Img6,
//   //     social: { linkedin: 'https://www.linkedin.com/in/kalabadorcas/', twitter: '#', email: 'dorcas@devlearnimpact.com' }
//   //   },
//   //   {
//   //     name: 'Chilombo Mwambazi',
//   //     role: 'Research Associate',
//   //     bio: 'MSc in Public Health (University of Zambia). Over 12 years in research, youth development, and program management; expertise in governance, clinical trials, RMNCAH, HIV/AIDS, and adolescent health.',
//   //     image: Img9,
//   //     social: { linkedin: 'https://www.linkedin.com/in/chilombo-mwambazi-b73973229', twitter: '#', email: 'chilombo@devlearnimpact.com' }
//   //   },
//   //   // {
//   //   //   name: 'Musadabwe Chulu',
//   //   //   role: 'Associate Consultant',
//   //   //   bio: 'Public Policy Analyst and Agricultural Economist with 18 years in policy, labor markets, and development programs. Master’s in Public Policy; advances evidence-based agriculture and social protection.',
//   //   //   image: '/images/team/musadabwe-chulu.jpg',
//   //   //   social: { linkedin: 'https://www.linkedin.com/in/musadabwe-chulu-2ba18a3a/', twitter: '#', email: 'musadabwe@devlearnimpact.com' }
//   //   // },
//   //   {
//   //     name: 'Chikondi Mkonda Mvula',
//   //     role: 'Associate Consultant',
//   //     bio: 'Master’s in Project Management; over 9 years in public health and WASH. Leads malaria, sanitation, and hygiene programs with participatory data and social accountability.',
//   //     image: Img17,
//   //     social: { linkedin: 'https://www.linkedin.com/in/chikondi-mkonda', twitter: '#', email: 'chikondi@devlearnimpact.com' }
//   //   },
//   //   // {
//   //   //   name: 'Nachizya Edith Namukanga',
//   //   //   role: 'Associate Consultant',
//   //   //   bio: 'Master’s in Public Health (Health Promotion); pursuing PhD in Public Health Epidemiology. Focus on adolescent health and menstrual management; supports health policy and program design.',
//   //   //   image: '/images/team/nachizya-namukanga.jpg',
//   //   //   social: { linkedin: 'https://www.linkedin.com/in/nachizyanamukanga', twitter: '#', email: 'nachizya@devlearnimpact.com' }
//   //   // },
//   //   // {
//   //   //   name: 'Kavuyi Kamelu-Soko',
//   //   //   role: 'Associate Consultant',
//   //   //   bio: 'Expertise in law, gender, and project management. Lecturer and Coordinator at University of Lusaka; leads EU-funded projects for inclusive, gender-responsive development.',
//   //   //   image: '/images/team/kavuyi-kamelu-soko.jpg',
//   //   //   social: { linkedin: '#', twitter: '#', email: 'kavuyi@devlearnimpact.com' }
//   //   // },
//   //   {
//   //     name: 'Tracy Muleya Mbandama',
//   //     role: 'Associate Consultant',
//   //     bio: 'Legal scholar in constitutional and human rights law, climate justice, and governance. Pursuing PhD in Law; supports rights-based advocacy and policy development.',
//   //     image: Img18,
//   //     social: { linkedin: 'https://www.linkedin.com/in/tracy-mbandama-0b386318b/', twitter: '#', email: 'tracy@devlearnimpact.com' }
//   //   },
//   //   // {
//   //   //   name: 'Brian Chonya',
//   //   //   role: 'Associate Consultant',
//   //   //   bio: 'Chartered Global Management Accountant with 20+ years in financial management and project accounting. Supports donor-funded projects for UN, USAID, and World Bank.',
//   //   //   image: '/images/team/brian-chonya.jpg',
//   //   //   social: { linkedin: 'https://www.linkedin.com/in/chonyabriancgma/', twitter: '#', email: 'brian.chonya@devlearnimpact.com' }
//   //   // },
//   //   // {
//   //   //   name: 'Modesta Phiri',
//   //   //   role: 'Associate Consultant',
//   //   //   bio: 'Social Protection Specialist with 10+ years in child and social protection programs. MSc in Project Management; advances gender equality and child rights at Ministry level.',
//   //   //   image: '/images/team/modesta-phiri.jpg',
//   //   //   social: { linkedin: 'https://www.linkedin.com/in/modesta-phiri-06a70064/', twitter: '#', email: 'modesta@devlearnimpact.com' }
//   //   // }
//   // ];

//   return (
//     <>
//       {/* Hero */}
//       <section className="hero">
//         <div className="container text-center">
//           <h1>Our Team</h1>
//           <p>
//             A multidisciplinary team of researchers, data scientists, MERL experts, and consultants committed to local ownership, evidence-based practice, and inclusive development in Zambia and beyond.
//           </p>
//           <div className="hero-buttons">
//             <a href="#leadership" className="btn btn-primary">
//               Meet Leadership <ArrowRight />
//             </a>
//             <a href="#seniors" className="btn btn-secondary">
//               Senior Associates
//             </a>
//           </div>
//         </div>
//       </section>

//       {/* Leadership */}
//       <section id="leadership" className="section leadership">
//         <div className="container text-center">
//           <h2>Leadership & Core Team</h2>
//           <p className="lead">
//             Guiding DevLearn with strategic vision, supported by experienced professionals in research, administration, and communication.
//           </p>
//           <div className="team-grid modern-grid">
//             {leadership.map((member, i) => (
//               <TeamMemberCard key={i} member={member} onClick={setSelectedMember} />
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Senior Associates */}
//       <section id="seniors" className="section seniors bg-light">
//         <div className="container text-center">
//           <h2>Associate Consultants</h2>
//           <p className="lead">
//             Experts in public health, policy, education, and more—bringing global perspectives to local impact.
//           </p>
//           <div className="team-grid modern-grid">
//             {seniorAssociates.map((member, i) => (
//               <TeamMemberCard key={i} member={member} onClick={setSelectedMember} />
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Associate Consultants */}
//       {/* <section id="associates" className="section associates">
//         <div className="container text-center">
//           <h2>Associate Consultants</h2>
//           <p className="lead">
//             A network of specialists in gender, health, policy, and finance—collaborating for evidence-driven solutions.
//           </p>
//           <div className="team-grid modern-grid">
//             {associateConsultants.map((member, i) => (
//               <TeamMemberCard key={i} member={member} onClick={setSelectedMember} />
//             ))}
//           </div>
//         </div>
//       </section> */}

//       {/* Modal */}
//       <MemberModal
//         member={selectedMember}
//         isOpen={!!selectedMember}
//         onClose={() => setSelectedMember(null)}
//       />

//       {/* CTA */}
//       <section className="final-cta text-center">
//         <div className="container">
//           <h2>Inspired by Our Team?<br />Let's Collaborate on Impactful Research.</h2>
//           <a href="/contact" className="btn btn-primary">
//             Get in Touch <ArrowRight size={28} />
//           </a>
//         </div>
//       </section>

//       {/* Footer */}
//       <footer className="footer">
//         <div className="footer-grid container">
//           <div>
//             <div className="footer-logo">DevLearn Impact Research Ltd</div>
//             <p>Plot No. 3788/A, Avondale Road, Lusaka</p>
//             <p>+260 973 902 981 / +260 978 341 825</p>
//             <p>info@devlearnimpact.com</p>
//           </div>
//           <div>
//             <h4>Services</h4>
//             <ul>
//               <li><a href="/services">Applied Research</a></li>
//               <li><a href="/services">MERL Frameworks</a></li>
//               <li><a href="/services">Capacity Building</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Thematic Areas</h4>
//             <ul>
//               <li><a href="/sectors">Education & Health</a></li>
//               <li><a href="/sectors">Governance & Climate</a></li>
//               <li><a href="/sectors">Economic Inclusion</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Company</h4>
//             <ul>
//               <li><a href="/about">About</a></li>
//               <li><a href="/team">Team</a></li>
//               <li><a href="/case-studies">Projects</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Connect</h4>
//             <ul>
//               <li><a href="https://www.linkedin.com/company/devlearn-impact-research/">LinkedIn</a></li>
//               <li><a href="https://www.facebook.com/share/1AS3Ahfsy7/">Facebook</a></li>
//             </ul>
//           </div>
//         </div>
//         <div className="copyright">
//           © 2026 DevLearn Impact Research Ltd. All rights reserved.
//         </div>
//       </footer>
//     </>
//   );
// };

// export default Team;
// // pages/Team/Team.jsx
// import { useState } from 'react';
// import { ArrowRight, Linkedin, Twitter, Mail, X } from 'lucide-react';
// import '../../index.css'; // global palette and variables
// import './Team.css'; // page-specific styles

// import Img4 from "../../Images/image4.png";
// import Img5 from "../../Images/image8.png";
// import Img6 from "../../Images/image12.png"; //dorcas
// import Img7 from "../../Images/image63.png"; //benjamin
// import Img8 from "../../Images/image60.png"; //benjamin
// import Img9 from "../../Images/image16.png"; //chilibombo
// import Img10 from "../../Images/image21.png"; //elizabeth
// import Img11 from "../../Images/image25.png"; //prof richard
// import Img12 from "../../Images/image35.png"; //zangose
// import Img13 from "../../Images/image30.png"; //abigail
// import Img14 from "../../Images/image40.png"; //silungwe
// // import Img15 from "../../Images/image43.png"; //hajara
// import Img16 from "../../Images/image43.png"; //hajara
// import Img17 from "../../Images/image50.png"; //chikondi
// import Img18 from "../../Images/image55.png"; //tracy




// const TeamMemberCard = ({ member, onClick }) => (
//   <div
//     className="team-member-card"
//     onClick={() => onClick(member)}
//     role="button"
//     tabIndex={0}
//     onKeyDown={(e) => e.key === 'Enter' && onClick(member)}
//   >
//     <div
//       className="member-image"
//       style={{
//         backgroundImage: member.image ? `url(${member.image})` : 'none',
//         backgroundSize: 'cover',
//         backgroundPosition: 'center'
//       }}
//     />
//     <div className="member-info">
//       <h3 className="member-name">{member.name}</h3>
//       <p className="member-role">{member.role}</p>
//     </div>
//   </div>
// );

// const MemberModal = ({ member, isOpen, onClose }) => {
//   if (!isOpen || !member) return null;

//   return (
//     <div className="modal-overlay" onClick={onClose}>
//       <div className="modal-content" onClick={e => e.stopPropagation()}>
//         <button className="modal-close" onClick={onClose}>
//           <X size={32} />
//         </button>

//         <div
//           className="modal-image"
//           style={{
//             backgroundImage: member.image ? `url(${member.image})` : 'none'
//           }}
//         />

//         <div className="modal-info">
//           <h2>{member.name}</h2>
//           <h3 className="modal-role">{member.role}</h3>
//           <p className="modal-bio">{member.bio}</p>

//           <div className="modal-socials">
//             {member.social?.linkedin && member.social.linkedin !== '#' && (
//               <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
//                 <Linkedin size={28} />
//               </a>
//             )}
//             {member.social?.twitter && member.social.twitter !== '#' && (
//               <a href={member.social.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter">
//                 <Twitter size={28} />
//               </a>
//             )}
//             {member.social?.email && (
//               <a href={`mailto:${member.social.email}`} aria-label="Email">
//                 <Mail size={28} />
//               </a>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// const Team = () => {
//   const [selectedMember, setSelectedMember] = useState(null);

//   const leadership = [
//     {
//       name: 'Richard Mwale',
//       role: 'Managing Director',
//       bio: 'BSc in Mathematics (Copperbelt University), MSc in Mathematical Sciences (Stellenbosch University). Over 5 years in research, consultancy, and teaching; specializing in applied statistics, data analytics, and predictive modeling. Provides strategic leadership for measurable impact.',
//       //image: '/images/team/richard-mwale.jpg', // ← Update with real image paths
//       image: Img4,
//       social: { linkedin: 'https://www.linkedin.com/in/richard-mwale', twitter: '#', email: 'richard@devlearnimpact.com' }
//     },
//     {
//       name: 'Brian Mumba',
//       role: 'Head of Research and Development',
//       bio: 'MSc in Educational Measurement and Evaluation; pursuing PhD and MA in Psychology. Over 7 years in research design, data analysis, and program management. Leads research strategy, evaluations, and capacity building.',
//       image: Img5,
//       social: { linkedin: 'https://www.linkedin.com/in/brianmumba/', twitter: '#', email: 'brian@devlearnimpact.com' }
//     },
//     {
//       name: 'Erick Dalitso Mwansa',
//       role: 'Business Development & Partnerships Manager',
//       bio: 'Bachelor of Laws (University of Lusaka). Experience in legal and corporate sectors; passionate about youth mentorship. Oversees executive support, governance, compliance, and organizational efficiency.',
//       image: Img8,
//       social: { linkedin: 'https://www.linkedin.com/in/juliet-phiri-glm365diay', twitter: '#', email: 'juliet@devlearnimpact.com' }
//     },
  
      
//     // {
//     //   name: 'Ndong’a Kamelu',
//     //   role: 'Research Associate',
//     //   bio: 'Economist and policy analyst with MSc in Economics and MBA in Finance. Over 10 years in research, strategy, and program evaluation; supports design, proposals, capacity building, and policy briefs.',
//     //   image: '/images/team/ndonga-kamelu.jpg',
//     //   social: { linkedin: 'https://www.linkedin.com/in/joshua-ndong-a-kamelu-9a20a71b7/', twitter: '#', email: 'ndonga@devlearnimpact.com' }
//     // },
//     {
//       name: 'Benjamin Lungu',
//       role: 'Knowledge Translation and Communication Consultant',
//       bio: 'BA in Library and Information Science & Psychology; Diploma in Marketing Management. Over 10 years in media production, creative design, and knowledge translation; supports communication and branding.',
//       image: Img7,
//       social: { linkedin: 'https://www.linkedin.com/in/benjamin-lungu-9447961b4', twitter: '#', email: 'benjamin@devlearnimpact.com' }
//     }
//   ];

//   const seniorAssociates = [
//     {
//       name: 'Professor Richard Mutemwa',
//       role: 'Senior Associate Consultant',
//       bio: 'PhD in Public Health, MSc in Medical Statistics, MBA in Public Health. Over 20 years in academia and consultancy; specializes in health systems, policy research, and statistical analysis.',
//       image: Img11,
//       social: { linkedin: '#', twitter: '#', email: 'richard.mutemwa@devlearnimpact.com' }
//     },
//     // {
//     //   name: 'Engineer Dr. William Mutonga',
//     //   role: 'Senior Associate Consultant',
//     //   bio: 'PhD in Entrepreneurship, MBA in Finance, BSc in Chemical Engineering. Over 15 years in Zambia’s extractives, energy, and environmental sectors; expertise in resource governance and policy.',
//     //   image: '/images/team/william-mutonga.jpg',
//     //   social: { linkedin: '#', twitter: '#', email: 'william@devlearnimpact.com' }
//     // },
//     {
//       name: 'Zangose Tembo Lumbwe',
//       role: 'Senior Associate Consultant',
//       bio: 'Master’s in International Development, Bachelor’s in International Relations. Over 10 years in gender equality, agriculture, food security, and climate resilience; supports inclusive initiatives.',
//       image: Img12,
//       social: { linkedin: 'https://www.linkedin.com/in/zangose-tembo-lumbwe-b68004122/', twitter: '#', email: 'zangose@devlearnimpact.com' }
//     },
//     {
//       name: 'Dr. Abigail Mukuwa Tuchili',
//       role: 'Senior Associate Consultant',
//       bio: 'Education leader specializing in curriculum development, learner-centered pedagogy, and inclusive education. Advises universities and schools; serves on key committees in nursing and education.',
//       image: Img13,
//       social: { linkedin: '#', twitter: '#', email: 'abigail@devlearnimpact.com' }
//     },
//     {
//       name: 'Dr. Wilson Silungwe',
//       role: 'Senior Associate Consultant',
//       bio: 'Communication and education expert with over 18 years in academia and consultancy. Pursuing PhD in Communication; supports strategy, policy analysis, and institutional capacity development.',
//       image: Img14,
//       social: { linkedin: 'https://www.linkedin.com/in/dr-wilson-silungwe-53a476254/', twitter: '#', email: 'wilson@devlearnimpact.com' }
//     },
//     // {
//     //   name: 'Dr. Pedro João Uetela',
//     //   role: 'Senior Associate Consultant',
//     //   bio: 'PhD in Social Sciences; postdoctoral fellowships in higher education policy. Over a decade in development sociology; advances inclusive education and policy innovation.',
//     //   image: '/images/team/pedro-joao-uetela.jpg',
//     //   social: { linkedin: 'https://www.linkedin.com/in/pedro-uetela-65393296/', twitter: '#', email: 'pedro@devlearnimpact.com' }
//     // },
//     {
//       name: 'Hajara Amina Athman',
//       role: 'Senior Associate Consultant',
//       bio: 'Master’s in Linguistics. Expertise in education systems, curriculum development, teacher training, and early-grade literacy; supports language and education reform.',
//       image: Img16,
//       social: { linkedin: 'https://www.linkedin.com/in/hajaraathman', twitter: '#', email: 'hajara@devlearnimpact.com' }
//     }
//   ];

//   const associateConsultants = [

//      {
//       name: 'Elizabeth Mwansa',
//       role: 'Associate Consultant',
//       bio: 'Safeguarding and gender professional with over 10 years in education, health, agriculture, and disaster response. Master’s in Peace, Leadership and Conflict Resolution; supports gender justice and inclusion.',
//       image: Img10,
//       social: { linkedin: 'https://www.linkedin.com/in/elizabeth-mwansa-627a43100/', twitter: '#', email: 'elizabeth@devlearnimpact.com' }
//     },
//       {
//       name: 'Dorcas Kalaba',
//       role: 'Research Associate',
//       bio: 'Licensed pharmacist; Master’s student in Epidemiology and Biostatistics. Expertise in research design, data analysis, and scientific writing. Supports study design, data collection, analysis, and proposals.',
//       image: Img6,
//       social: { linkedin: 'https://www.linkedin.com/in/kalabadorcas/', twitter: '#', email: 'dorcas@devlearnimpact.com' }
//     },
//     {
//       name: 'Chilombo Mwambazi',
//       role: 'Research Associate',
//       bio: 'MSc in Public Health (University of Zambia). Over 12 years in research, youth development, and program management; expertise in governance, clinical trials, RMNCAH, HIV/AIDS, and adolescent health.',
//       image: Img9,
//       social: { linkedin: 'https://www.linkedin.com/in/chilombo-mwambazi-b73973229', twitter: '#', email: 'chilombo@devlearnimpact.com' }
//     },
//     // {
//     //   name: 'Musadabwe Chulu',
//     //   role: 'Associate Consultant',
//     //   bio: 'Public Policy Analyst and Agricultural Economist with 18 years in policy, labor markets, and development programs. Master’s in Public Policy; advances evidence-based agriculture and social protection.',
//     //   image: '/images/team/musadabwe-chulu.jpg',
//     //   social: { linkedin: 'https://www.linkedin.com/in/musadabwe-chulu-2ba18a3a/', twitter: '#', email: 'musadabwe@devlearnimpact.com' }
//     // },
//     {
//       name: 'Chikondi Mkonda Mvula',
//       role: 'Associate Consultant',
//       bio: 'Master’s in Project Management; over 9 years in public health and WASH. Leads malaria, sanitation, and hygiene programs with participatory data and social accountability.',
//       image: Img17,
//       social: { linkedin: 'https://www.linkedin.com/in/chikondi-mkonda', twitter: '#', email: 'chikondi@devlearnimpact.com' }
//     },
//     // {
//     //   name: 'Nachizya Edith Namukanga',
//     //   role: 'Associate Consultant',
//     //   bio: 'Master’s in Public Health (Health Promotion); pursuing PhD in Public Health Epidemiology. Focus on adolescent health and menstrual management; supports health policy and program design.',
//     //   image: '/images/team/nachizya-namukanga.jpg',
//     //   social: { linkedin: 'https://www.linkedin.com/in/nachizyanamukanga', twitter: '#', email: 'nachizya@devlearnimpact.com' }
//     // },
//     // {
//     //   name: 'Kavuyi Kamelu-Soko',
//     //   role: 'Associate Consultant',
//     //   bio: 'Expertise in law, gender, and project management. Lecturer and Coordinator at University of Lusaka; leads EU-funded projects for inclusive, gender-responsive development.',
//     //   image: '/images/team/kavuyi-kamelu-soko.jpg',
//     //   social: { linkedin: '#', twitter: '#', email: 'kavuyi@devlearnimpact.com' }
//     // },
//     {
//       name: 'Tracy Muleya Mbandama',
//       role: 'Associate Consultant',
//       bio: 'Legal scholar in constitutional and human rights law, climate justice, and governance. Pursuing PhD in Law; supports rights-based advocacy and policy development.',
//       image: Img18,
//       social: { linkedin: 'https://www.linkedin.com/in/tracy-mbandama-0b386318b/', twitter: '#', email: 'tracy@devlearnimpact.com' }
//     },
//     // {
//     //   name: 'Brian Chonya',
//     //   role: 'Associate Consultant',
//     //   bio: 'Chartered Global Management Accountant with 20+ years in financial management and project accounting. Supports donor-funded projects for UN, USAID, and World Bank.',
//     //   image: '/images/team/brian-chonya.jpg',
//     //   social: { linkedin: 'https://www.linkedin.com/in/chonyabriancgma/', twitter: '#', email: 'brian.chonya@devlearnimpact.com' }
//     // },
//     // {
//     //   name: 'Modesta Phiri',
//     //   role: 'Associate Consultant',
//     //   bio: 'Social Protection Specialist with 10+ years in child and social protection programs. MSc in Project Management; advances gender equality and child rights at Ministry level.',
//     //   image: '/images/team/modesta-phiri.jpg',
//     //   social: { linkedin: 'https://www.linkedin.com/in/modesta-phiri-06a70064/', twitter: '#', email: 'modesta@devlearnimpact.com' }
//     // }
//   ];

//   return (
//     <>
//       {/* Hero */}
//       <section className="hero">
//         <div className="container text-center">
//           <h1>Our Team</h1>
//           <p>
//             A multidisciplinary team of researchers, data scientists, MERL experts, and consultants committed to local ownership, evidence-based practice, and inclusive development in Zambia and beyond.
//           </p>
//           <div className="hero-buttons">
//             <a href="#leadership" className="btn btn-primary">
//               Meet Leadership <ArrowRight />
//             </a>
//             <a href="#seniors" className="btn btn-secondary">
//               Senior Associates
//             </a>
//           </div>
//         </div>
//       </section>

//       {/* Leadership */}
//       <section id="leadership" className="section leadership">
//         <div className="container text-center">
//           <h2>Leadership & Core Team</h2>
//           <p className="lead">
//             Guiding DevLearn with strategic vision, supported by experienced professionals in research, administration, and communication.
//           </p>
//           <div className="team-grid modern-grid">
//             {leadership.map((member, i) => (
//               <TeamMemberCard key={i} member={member} onClick={setSelectedMember} />
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Senior Associates */}
//       <section id="seniors" className="section seniors bg-light">
//         <div className="container text-center">
//           <h2>Senior Associate Consultants</h2>
//           <p className="lead">
//             Experts in public health, policy, education, and more—bringing global perspectives to local impact.
//           </p>
//           <div className="team-grid modern-grid">
//             {seniorAssociates.map((member, i) => (
//               <TeamMemberCard key={i} member={member} onClick={setSelectedMember} />
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Associate Consultants */}
//       <section id="associates" className="section associates">
//         <div className="container text-center">
//           <h2>Associate Consultants</h2>
//           <p className="lead">
//             A network of specialists in gender, health, policy, and finance—collaborating for evidence-driven solutions.
//           </p>
//           <div className="team-grid modern-grid">
//             {associateConsultants.map((member, i) => (
//               <TeamMemberCard key={i} member={member} onClick={setSelectedMember} />
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Modal */}
//       <MemberModal
//         member={selectedMember}
//         isOpen={!!selectedMember}
//         onClose={() => setSelectedMember(null)}
//       />

//       {/* CTA */}
//       <section className="final-cta text-center">
//         <div className="container">
//           <h2>Inspired by Our Team?<br />Let's Collaborate on Impactful Research.</h2>
//           <a href="/contact" className="btn btn-primary">
//             Get in Touch <ArrowRight size={28} />
//           </a>
//         </div>
//       </section>

//       {/* Footer */}
//       <footer className="footer">
//         <div className="footer-grid container">
//           <div>
//             <div className="footer-logo">DevLearn Impact Research Ltd</div>
//             <p>Plot No. 3788/A, Avondale Road, Lusaka</p>
//             <p>+260 973 902 981 / +260 978 341 825</p>
//             <p>info@devlearnimpact.com</p>
//           </div>
//           <div>
//             <h4>Services</h4>
//             <ul>
//               <li><a href="/services">Applied Research</a></li>
//               <li><a href="/services">MERL Frameworks</a></li>
//               <li><a href="/services">Capacity Building</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Thematic Areas</h4>
//             <ul>
//               <li><a href="/sectors">Education & Health</a></li>
//               <li><a href="/sectors">Governance & Climate</a></li>
//               <li><a href="/sectors">Economic Inclusion</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Company</h4>
//             <ul>
//               <li><a href="/about">About</a></li>
//               <li><a href="/team">Team</a></li>
//               <li><a href="/case-studies">Projects</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Connect</h4>
//             <ul>
//               <li><a href="https://www.linkedin.com/company/devlearn-impact-research/">LinkedIn</a></li>
//               <li><a href="https://www.facebook.com/share/1AS3Ahfsy7/">Facebook</a></li>
//             </ul>
//           </div>
//         </div>
//         <div className="copyright">
//           © 2026 DevLearn Impact Research Ltd. All rights reserved.
//         </div>
//       </footer>
//     </>
//   );
// };

// export default Team;


// // pages/Team/Team.jsx
// import { ArrowRight, Linkedin, Twitter, Mail } from 'lucide-react';
// import '../../index.css'; // global palette and variables
// import './Team.css'; // page-specific styles

// const TeamCard = ({ member }) => (
//   <div className="team-card">
//     <div
//       className="team-image"
//       style={{ backgroundImage: member.image ? `url(${member.image})` : 'none' }}
//     ></div>
//     <h3>{member.name}</h3>
//     <p className="role">{member.role}</p>
//     <p className="bio">{member.bio}</p>
//     <div className="social-links">
//       {member.social.linkedin && (
//         <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
//           <Linkedin size={20} />
//         </a>
//       )}
//       {member.social.twitter && (
//         <a href={member.social.twitter} aria-label="Twitter">
//           <Twitter size={20} />
//         </a>
//       )}
//       {member.social.email && (
//         <a href={`mailto:${member.social.email}`} aria-label="Email">
//           <Mail size={20} />
//         </a>
//       )}
//     </div>
//   </div>
// );

// const Team = () => {
//   const leadership = [
//     {
//       name: 'Richard Mwale',
//       role: 'Managing Director',
//       bio: 'BSc in Mathematics (Copperbelt University), MSc in Mathematical Sciences (Stellenbosch University). Over 5 years in research, consultancy, and teaching; specializing in applied statistics, data analytics, and predictive modeling. Provides strategic leadership for measurable impact.',
//       social: { linkedin: 'https://www.linkedin.com/in/richard-mwale', twitter: '#', email: 'richard@devlearnimpact.com' }
//     },
//     {
//       name: 'Brian Mumba',
//       role: 'Head of Research and Development',
//       bio: 'MSc in Educational Measurement and Evaluation; pursuing PhD and MA in Psychology. Over 7 years in research design, data analysis, and program management. Leads research strategy, evaluations, and capacity building.',
//       social: { linkedin: 'https://www.linkedin.com/in/brianmumba/', twitter: '#', email: 'brian@devlearnimpact.com' }
//     },
//     {
//       name: 'Juliet Phiri',
//       role: 'Administrative Manager',
//       bio: 'Bachelor of Laws (University of Lusaka). Experience in legal and corporate sectors; passionate about youth mentorship. Oversees executive support, governance, compliance, and organizational efficiency.',
//       social: { linkedin: 'https://www.linkedin.com/in/juliet-phiri-glm365diay', twitter: '#', email: 'juliet@devlearnimpact.com' }
//     },
//     // ... add other leadership members here
//   ];

//   const seniorAssociates = [
//     {
//       name: 'Professor Richard Mutemwa',
//       role: 'Senior Associate Consultant',
//       bio: 'PhD in Public Health, MSc in Medical Statistics, MBA in Public Health. Over 20 years in academia and consultancy; specializes in health systems, policy research, and statistical analysis.',
//       social: { linkedin: '#', twitter: '#', email: 'richard.mutemwa@devlearnimpact.com' }
//     },
//     // ... add other senior associates
//   ];

//   const associateConsultants = [
//     {
//       name: 'Elizabeth Mwansa',
//       role: 'Associate Consultant',
//       bio: 'Safeguarding and gender professional with over 10 years in education, health, agriculture, and disaster response. Master’s in Peace, Leadership and Conflict Resolution; supports gender justice and inclusion.',
//       social: { linkedin: 'https://www.linkedin.com/in/elizabeth-mwansa-627a43100/', twitter: '#', email: 'elizabeth@devlearnimpact.com' }
//     },
//     // ... add other associates
//   ];

//   return (
//     <>
//       {/* Hero */}
//       <section className="hero">
//         <div className="container text-center">
//           <h1>Our Team</h1>
//           <p>
//             A multidisciplinary team of researchers, data scientists, MERL experts, and consultants committed to local ownership, evidence-based practice, and inclusive development in Zambia and beyond.
//           </p>
//           <div className="hero-buttons">
//             <a href="#leadership" className="btn btn-primary">
//               Meet Leadership <ArrowRight />
//             </a>
//             <a href="#seniors" className="btn btn-secondary">
//               Senior Associates
//             </a>
//           </div>
//         </div>
//       </section>

//       {/* Leadership */}
//       <section id="leadership" className="section leadership">
//         <div className="container text-center">
//           <h2>Leadership & Core Team</h2>
//           <p className="lead">
//             Guiding DevLearn with strategic vision, supported by experienced professionals in research, administration, and communication.
//           </p>
//           <div className="team-grid">
//             {leadership.map((member, i) => <TeamCard key={i} member={member} />)}
//           </div>
//         </div>
//       </section>

//       {/* Senior Associates */}
//       <section id="seniors" className="section seniors bg-light">
//         <div className="container text-center">
//           <h2>Senior Associate Consultants</h2>
//           <p className="lead">
//             Experts in public health, policy, education, and more—bringing global perspectives to local impact.
//           </p>
//           <div className="team-grid">
//             {seniorAssociates.map((member, i) => <TeamCard key={i} member={member} />)}
//           </div>
//         </div>
//       </section>

//       {/* Associate Consultants */}
//       <section id="associates" className="section associates">
//         <div className="container text-center">
//           <h2>Associate Consultants</h2>
//           <p className="lead">
//             A network of specialists in gender, health, policy, and finance—collaborating for evidence-driven solutions.
//           </p>
//           <div className="team-grid">
//             {associateConsultants.map((member, i) => <TeamCard key={i} member={member} />)}
//           </div>
//         </div>
//       </section>

//       {/* CTA */}
//       <section className="final-cta text-center">
//         <div className="container">
//           <h2>Inspired by Our Team?<br />Let's Collaborate on Impactful Research.</h2>
//           <a href="/contact" className="btn btn-primary">
//             Get in Touch <ArrowRight size={28} />
//           </a>
//         </div>
//       </section>

//       {/* Footer */}
//       <footer className="footer">
//         <div className="footer-grid container">
//           <div>
//             <div className="footer-logo">DevLearn Impact Research Ltd</div>
//             <p>Plot No. 3788/A, Avondale Road, Lusaka</p>
//             <p>+260 973 902 981 / +260 978 341 825</p>
//             <p>info@devlearnimpact.com</p>
//           </div>
//           <div>
//             <h4>Services</h4>
//             <ul>
//               <li><a href="/services">Applied Research</a></li>
//               <li><a href="/services">MERL Frameworks</a></li>
//               <li><a href="/services">Capacity Building</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Thematic Areas</h4>
//             <ul>
//               <li><a href="/sectors">Education & Health</a></li>
//               <li><a href="/sectors">Governance & Climate</a></li>
//               <li><a href="/sectors">Economic Inclusion</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Company</h4>
//             <ul>
//               <li><a href="/about">About</a></li>
//               <li><a href="/team">Team</a></li>
//               <li><a href="/case-studies">Projects</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Connect</h4>
//             <ul>
//               <li><a href="https://www.linkedin.com/company/devlearn-impact-research/">LinkedIn</a></li>
//               <li><a href="https://www.facebook.com/share/1AS3Ahfsy7/">Facebook</a></li>
//             </ul>
//           </div>
//         </div>
//         <div className="copyright">
//           © 2025 DevLearn Impact Research Ltd. All rights reserved. Registered in Zambia, June 28, 2025.
//         </div>
//       </footer>
//     </>
//   );
// };

// export default Team;


// // pages/Team/Team.jsx - Team page adapted for DevLearn (from PDF pages 15-23)
// import { ArrowRight, Users, Linkedin, Twitter, Mail } from 'lucide-react';
// import '../../index.css'; // Shared global styles with palette
// import './Team.css'; // Page-specific styles

// const Team = () => {
//   const leadership = [
//     {
//       name: 'Richard Mwale',
//       role: 'Managing Director',
//       bio: 'BSc in Mathematics (Copperbelt University), MSc in Mathematical Sciences (Stellenbosch University). Over 5 years in research, consultancy, and teaching; specializing in applied statistics, data analytics, and predictive modeling. Provides strategic leadership for measurable impact.',
//       social: { linkedin: 'https://www.linkedin.com/in/richard-mwale', twitter: '#', email: 'richard@devlearnimpact.com' }
//     },
//     {
//       name: 'Brian Mumba',
//       role: 'Head of Research and Development',
//       bio: 'MSc in Educational Measurement and Evaluation; pursuing PhD and MA in Psychology. Over 7 years in research design, data analysis, and program management. Leads research strategy, evaluations, and capacity building.',
//       social: { linkedin: 'https://www.linkedin.com/in/brianmumba/', twitter: '#', email: 'brian@devlearnimpact.com' }
//     },
//     {
//       name: 'Juliet Phiri',
//       role: 'Administrative Manager',
//       bio: 'Bachelor of Laws (University of Lusaka). Experience in legal and corporate sectors; passionate about youth mentorship. Oversees executive support, governance, compliance, and organizational efficiency.',
//       social: { linkedin: 'https://www.linkedin.com/in/juliet-phiri-glm365diay', twitter: '#', email: 'juliet@devlearnimpact.com' }
//     },
//     {
//       name: 'Dorcas Kalaba',
//       role: 'Research Associate',
//       bio: 'Licensed pharmacist; Master’s student in Epidemiology and Biostatistics. Expertise in research design, data analysis, and scientific writing. Supports study design, data collection, analysis, and proposals.',
//       social: { linkedin: 'https://www.linkedin.com/in/kalabadorcas/', twitter: '#', email: 'dorcas@devlearnimpact.com' }
//     },
//     {
//       name: 'Chilombo Mwambazi',
//       role: 'Research Associate',
//       bio: 'MSc in Public Health (University of Zambia). Over 12 years in research, youth development, and program management; expertise in governance, clinical trials, RMNCAH, HIV/AIDS, and adolescent health.',
//       social: { linkedin: 'https://www.linkedin.com/in/chilombo-mwambazi-b73973229', twitter: '#', email: 'chilombo@devlearnimpact.com' }
//     },
//     {
//       name: 'Ndong’a Kamelu',
//       role: 'Research Associate',
//       bio: 'Economist and policy analyst with MSc in Economics and MBA in Finance. Over 10 years in research, strategy, and program evaluation; supports design, proposals, capacity building, and policy briefs.',
//       social: { linkedin: 'https://www.linkedin.com/in/joshua-ndong-a-kamelu-9a20a71b7/', twitter: '#', email: 'ndonga@devlearnimpact.com' }
//     },
//     {
//       name: 'Benjamin Lungu',
//       role: 'Knowledge Translation and Communication Consultant',
//       bio: 'BA in Library and Information Science & Psychology; Diploma in Marketing Management. Over 10 years in media production, creative design, and knowledge translation; supports communication and branding.',
//       social: { linkedin: 'https://www.linkedin.com/in/benjamin-lungu-9447961b4', twitter: '#', email: 'benjamin@devlearnimpact.com' }
//     }
//   ];

//   const seniorAssociates = [
//     {
//       name: 'Professor Richard Mutemwa',
//       role: 'Senior Associate Consultant',
//       bio: 'PhD in Public Health, MSc in Medical Statistics, MBA in Public Health. Over 20 years in academia and consultancy; specializes in health systems, policy research, and statistical analysis.',
//       social: { linkedin: '#', twitter: '#', email: 'richard.mutemwa@devlearnimpact.com' }
//     },
//     {
//       name: 'Engineer Dr. William Mutonga',
//       role: 'Senior Associate Consultant',
//       bio: 'PhD in Entrepreneurship, MBA in Finance, BSc in Chemical Engineering. Over 15 years in Zambia’s extractives, energy, and environmental sectors; expertise in resource governance and policy.',
//       social: { linkedin: '#', twitter: '#', email: 'william@devlearnimpact.com' }
//     },
//     {
//       name: 'Zangose Tembo Lumbwe',
//       role: 'Senior Associate Consultant',
//       bio: 'Master’s in International Development, Bachelor’s in International Relations. Over 10 years in gender equality, agriculture, food security, and climate resilience; supports inclusive initiatives.',
//       social: { linkedin: 'https://www.linkedin.com/in/zangose-tembo-lumbwe-b68004122/', twitter: '#', email: 'zangose@devlearnimpact.com' }
//     },
//     {
//       name: 'Dr. Abigail Mukuwa Tuchili',
//       role: 'Senior Associate Consultant',
//       bio: 'Education leader specializing in curriculum development, learner-centered pedagogy, and inclusive education. Advises universities and schools; serves on key committees in nursing and education.',
//       social: { linkedin: '#', twitter: '#', email: 'abigail@devlearnimpact.com' }
//     },
//     {
//       name: 'Dr. Wilson Silungwe',
//       role: 'Senior Associate Consultant',
//       bio: 'Communication and education expert with over 18 years in academia and consultancy. Pursuing PhD in Communication; supports strategy, policy analysis, and institutional capacity development.',
//       social: { linkedin: 'https://www.linkedin.com/in/dr-wilson-silungwe-53a476254/', twitter: '#', email: 'wilson@devlearnimpact.com' }
//     },
//     {
//       name: 'Dr. Pedro João Uetela',
//       role: 'Senior Associate Consultant',
//       bio: 'PhD in Social Sciences; postdoctoral fellowships in higher education policy. Over a decade in development sociology; advances inclusive education and policy innovation.',
//       social: { linkedin: 'https://www.linkedin.com/in/pedro-uetela-65393296/', twitter: '#', email: 'pedro@devlearnimpact.com' }
//     },
//     {
//       name: 'Hajara Amina Athman',
//       role: 'Senior Associate Consultant',
//       bio: 'Master’s in Linguistics. Expertise in education systems, curriculum development, teacher training, and early-grade literacy; supports language and education reform.',
//       social: { linkedin: 'https://www.linkedin.com/in/hajaraathman', twitter: '#', email: 'hajara@devlearnimpact.com' }
//     }
//   ];

//   const associateConsultants = [
//     {
//       name: 'Elizabeth Mwansa',
//       role: 'Associate Consultant',
//       bio: 'Safeguarding and gender professional with over 10 years in education, health, agriculture, and disaster response. Master’s in Peace, Leadership and Conflict Resolution; supports gender justice and inclusion.',
//       social: { linkedin: 'https://www.linkedin.com/in/elizabeth-mwansa-627a43100/', twitter: '#', email: 'elizabeth@devlearnimpact.com' }
//     },
//     {
//       name: 'Musadabwe Chulu',
//       role: 'Associate Consultant',
//       bio: 'Public Policy Analyst and Agricultural Economist with 18 years in policy, labor markets, and development programs. Master’s in Public Policy; advances evidence-based agriculture and social protection.',
//       social: { linkedin: 'https://www.linkedin.com/in/musadabwe-chulu-2ba18a3a/', twitter: '#', email: 'musadabwe@devlearnimpact.com' }
//     },
//     {
//       name: 'Chikondi Mkonda Mvula',
//       role: 'Associate Consultant',
//       bio: 'Master’s in Project Management; over 9 years in public health and WASH. Leads malaria, sanitation, and hygiene programs with participatory data and social accountability.',
//       social: { linkedin: 'https://www.linkedin.com/in/chikondi-mkonda', twitter: '#', email: 'chikondi@devlearnimpact.com' }
//     },
//     {
//       name: 'Nachizya Edith Namukanga',
//       role: 'Associate Consultant',
//       bio: 'Master’s in Public Health (Health Promotion); pursuing PhD in Public Health Epidemiology. Focus on adolescent health and menstrual management; supports health policy and program design.',
//       social: { linkedin: 'https://www.linkedin.com/in/nachizyanamukanga', twitter: '#', email: 'nachizya@devlearnimpact.com' }
//     },
//     {
//       name: 'Kavuyi Kamelu-Soko',
//       role: 'Associate Consultant',
//       bio: 'Expertise in law, gender, and project management. Lecturer and Coordinator at University of Lusaka; leads EU-funded projects for inclusive, gender-responsive development.',
//       social: { linkedin: '#', twitter: '#', email: 'kavuyi@devlearnimpact.com' }
//     },
//     {
//       name: 'Tracy Muleya Mbandama',
//       role: 'Associate Consultant',
//       bio: 'Legal scholar in constitutional and human rights law, climate justice, and governance. Pursuing PhD in Law; supports rights-based advocacy and policy development.',
//       social: { linkedin: 'https://www.linkedin.com/in/tracy-mbandama-0b386318b/', twitter: '#', email: 'tracy@devlearnimpact.com' }
//     },
//     {
//       name: 'Brian Chonya',
//       role: 'Associate Consultant',
//       bio: 'Chartered Global Management Accountant with 20+ years in financial management and project accounting. Supports donor-funded projects for UN, USAID, and World Bank.',
//       social: { linkedin: 'https://www.linkedin.com/in/chonyabriancgma/', twitter: '#', email: 'brian.chonya@devlearnimpact.com' }
//     },
//     {
//       name: 'Modesta Phiri',
//       role: 'Associate Consultant',
//       bio: 'Social Protection Specialist with 10+ years in child and social protection programs. MSc in Project Management; advances gender equality and child rights at Ministry level.',
//       social: { linkedin: 'https://www.linkedin.com/in/modesta-phiri-06a70064/', twitter: '#', email: 'modesta@devlearnimpact.com' }
//     }
//   ];

//   return (
//     <>
//       {/* Hero */}
//       <section className="hero" style={{ background: 'linear-gradient(180deg, #F0F9FF 0%, #ffffff 100%)' }}>
//         <div className="container">
//           <h1 style={{ color: '#1E3A8A' }}>Our Team</h1>
//           <p>
//             A multidisciplinary team of researchers, data scientists, MERL experts, and consultants committed to local ownership, evidence-based practice, and inclusive development in Zambia and beyond.
//           </p>
//           <div className="hero-buttons">
//             <a href="#leadership" className="btn btn-primary">
//               Meet Leadership <ArrowRight />
//             </a>
//             <a href="#seniors" className="btn btn-secondary">
//               Senior Associates
//             </a>
//           </div>
//         </div>
//       </section>

//       {/* Leadership Section */}
//       <section id="leadership" className="section leadership">
//         <div className="container">
//           <div className="text-center">
//             <h2 style={{ color: '#1E3A8A' }}>Leadership & Core Team</h2>
//             <p className="lead">
//               Guiding DevLearn with strategic vision, supported by experienced professionals in research, administration, and communication.
//             </p>
//           </div>
//           <div className="team-grid">
//             {leadership.map((member, index) => (
//               <div key={index} className="team-card">
//                 <div className="team-image" style={{ backgroundImage: 'none' }}></div> {/* Add real images via url */}
//                 <h3 style={{ color: '#1E3A8A' }}>{member.name}</h3>
//                 <p className="role">{member.role}</p>
//                 <p className="bio">{member.bio}</p>
//                 <div className="social-links">
//                   <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><Linkedin size={20} /></a>
//                   <a href={member.social.twitter} aria-label="Twitter"><Twitter size={20} /></a>
//                   <a href={`mailto:${member.social.email}`} aria-label="Email"><Mail size={20} /></a>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Senior Associates Section */}
//       <section id="seniors" className="section seniors bg-light" style={{ background: '#F0F9FF' }}>
//         <div className="container">
//           <div className="text-center">
//             <h2 style={{ color: '#1E3A8A' }}>Senior Associate Consultants</h2>
//             <p className="lead">
//               Experts in public health, policy, education, and more—bringing global perspectives to local impact.
//             </p>
//           </div>
//           <div className="team-grid">
//             {seniorAssociates.map((member, index) => (
//               <div key={index} className="team-card">
//                 <div className="team-image" style={{ backgroundImage: 'none' }}></div>
//                 <h3 style={{ color: '#1E3A8A' }}>{member.name}</h3>
//                 <p className="role">{member.role}</p>
//                 <p className="bio">{member.bio}</p>
//                 <div className="social-links">
//                   <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><Linkedin size={20} /></a>
//                   <a href={member.social.twitter} aria-label="Twitter"><Twitter size={20} /></a>
//                   <a href={`mailto:${member.social.email}`} aria-label="Email"><Mail size={20} /></a>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Associate Consultants Section */}
//       <section id="associates" className="section associates">
//         <div className="container">
//           <div className="text-center">
//             <h2 style={{ color: '#1E3A8A' }}>Associate Consultants</h2>
//             <p className="lead">
//               A network of specialists in gender, health, policy, and finance—collaborating for evidence-driven solutions.
//             </p>
//           </div>
//           <div className="team-grid">
//             {associateConsultants.map((member, index) => (
//               <div key={index} className="team-card">
//                 <div className="team-image" style={{ backgroundImage: 'none' }}></div>
//                 <h3 style={{ color: '#1E3A8A' }}>{member.name}</h3>
//                 <p className="role">{member.role}</p>
//                 <p className="bio">{member.bio}</p>
//                 <div className="social-links">
//                   <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><Linkedin size={20} /></a>
//                   <a href={member.social.twitter} aria-label="Twitter"><Twitter size={20} /></a>
//                   <a href={`mailto:${member.social.email}`} aria-label="Email"><Mail size={20} /></a>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* CTA */}
//       <section className="final-cta text-center" style={{ background: '#1E3A8A', color: '#fff' }}>
//         <div className="container">
//           <h2>
//             Inspired by Our Team?<br />Let's Collaborate on Impactful Research.
//           </h2>
//           <a href="/contact" className="btn btn-primary" style={{ background: '#fff', color: '#1E3A8A' }}>
//             Get in Touch <ArrowRight size={28} />
//           </a>
//         </div>
//       </section>

//       {/* Footer - Reused with DevLearn details */}
//       <footer className="footer" style={{ background: '#111827' }}>
//         <div className="footer-grid">
//           <div>
//             <div className="footer-logo" style={{ color: '#0D9488' }}>DevLearn Impact Research Ltd</div>
//             <p>Plot No. 3788/A, Avondale Road, Lusaka</p>
//             <p>+260 973 902 981 / +260 978 341 825</p>
//             <p>info@devlearnimpact.com</p>
//           </div>
//           <div>
//             <h4 style={{ color: '#fff' }}>Services</h4>
//             <ul>
//               <li><a href="/services">Applied Research</a></li>
//               <li><a href="/services">MERL Frameworks</a></li>
//               <li><a href="/services">Capacity Building</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4 style={{ color: '#fff' }}>Thematic Areas</h4>
//             <ul>
//               <li><a href="/sectors">Education & Health</a></li>
//               <li><a href="/sectors">Governance & Climate</a></li>
//               <li><a href="/sectors">Economic Inclusion</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4 style={{ color: '#fff' }}>Company</h4>
//             <ul>
//               <li><a href="/about">About</a></li>
//               <li><a href="/team">Team</a></li>
//               <li><a href="/case-studies">Projects</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4 style={{ color: '#fff' }}>Connect</h4>
//             <ul>
//               <li><a href="https://www.linkedin.com/company/devlearn-impact-research/">LinkedIn</a></li>
//               <li><a href="https://www.facebook.com/share/1AS3Ahfsy7/">Facebook</a></li>
//             </ul>
//           </div>
//         </div>
//         <div className="copyright" style={{ color: '#9CA3AF' }}>
//           © 2025 DevLearn Impact Research Ltd. All rights reserved. Registered in Zambia, June 28, 2025.
//         </div>
//       </footer>
//     </>
//   );
// };

// export default Team;

// // pages/Team/Team.jsx - Team page
// import { ArrowRight, Users, Linkedin, Twitter, Mail } from 'lucide-react';
// import '../../index.css'; // Shared global styles
// import './Team.css'; // Page-specific styles

// const Team = () => {
//   const teamMembers = [
//     {
//       name: 'Dr. Aisha Mwangi',
//       role: 'Founder & CEO',
//       bio: 'PhD in Development Economics. 15+ years shaping policy in Africa and beyond. Passionate about equitable research.',
//       image: '', // Placeholder: add backgroundImage: 'url(path/to/aisha.jpg)'
//       social: { linkedin: '#', twitter: '#', email: 'aisha@insightresearch.com' }
//     },
//     {
//       name: 'Prof. Liam Chen',
//       role: 'Chief Research Officer',
//       bio: 'Expert in quantitative methods. Led 200+ studies across health and education. Advocate for data-driven innovation.',
//       image: '',
//       social: { linkedin: '#', twitter: '#', email: 'liam@insightresearch.com' }
//     },
//     {
//       name: 'Sarah Patel',
//       role: 'Director of Operations',
//       bio: 'Strategic partnerships specialist. Scaled operations to 50+ global collaborators. Focus on ethical scaling.',
//       image: '',
//       social: { linkedin: '#', twitter: '#', email: 'sarah@insightresearch.com' }
//     },
//     {
//       name: 'Dr. Elena Rodriguez',
//       role: 'Lead Data Scientist',
//       bio: 'Specialist in AI and machine learning for social impact. 10+ years in predictive analytics for NGOs.',
//       image: '',
//       social: { linkedin: '#', twitter: '#', email: 'elena@insightresearch.com' }
//     },
//     {
//       name: 'Marcus Osei',
//       role: 'Senior Policy Analyst',
//       bio: 'MA in Public Policy. Expertise in governance and finance sectors. 8 years influencing African policy frameworks.',
//       image: '',
//       social: { linkedin: '#', twitter: '#', email: 'marcus@insightresearch.com' }
//     },
//     {
//       name: 'Jia Kim',
//       role: 'Field Research Coordinator',
//       bio: 'Background in anthropology. Manages multi-country fieldwork with a focus on inclusive data collection.',
//       image: '',
//       social: { linkedin: '#', twitter: '#', email: 'jia@insightresearch.com' }
//     }
//   ];

//   return (
//     <>
//       {/* Hero */}
//       <section className="hero">
//         <div className="container">
//           <h1>Our Team</h1>
//           <p>
//             A diverse collective of researchers, analysts, and strategists dedicated to turning data into
//             meaningful change. With expertise spanning continents and disciplines, we're your partners in insight.
//           </p>
//           <div className="hero-buttons">
//             <a href="#leadership" className="btn btn-primary">
//               Meet Leadership <ArrowRight />
//             </a>
//             <a href="#experts" className="btn btn-secondary">
//               Our Experts
//             </a>
//           </div>
//         </div>
//       </section>

//       {/* Leadership Section */}
//       <section id="leadership" className="section leadership">
//         <div className="container">
//           <div className="text-center">
//             <h2>Leadership Team</h2>
//             <p className="lead">
//               Visionaries steering InSight Research toward global impact. Their expertise defines our direction.
//             </p>
//           </div>
//           <div className="team-grid">
//             {teamMembers.slice(0, 3).map((member, index) => (
//               <div key={index} className="team-card">
//                 <div className="team-image" style={{ backgroundImage: member.image ? `url(${member.image})` : 'none' }}></div>
//                 <h3>{member.name}</h3>
//                 <p className="role">{member.role}</p>
//                 <p className="bio">{member.bio}</p>
//                 <div className="social-links">
//                   <a href={member.social.linkedin} aria-label="LinkedIn"><Linkedin size={20} /></a>
//                   <a href={member.social.twitter} aria-label="Twitter"><Twitter size={20} /></a>
//                   <a href={`mailto:${member.social.email}`} aria-label="Email"><Mail size={20} /></a>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* Experts Section */}
//       <section id="experts" className="section experts bg-light">
//         <div className="container">
//           <div className="text-center">
//             <h2>Our Research Experts</h2>
//             <p className="lead">
//               Hands-on specialists driving every project. Their fieldwork and analysis bring our research to life.
//             </p>
//           </div>
//           <div className="team-grid">
//             {teamMembers.slice(3).map((member, index) => (
//               <div key={index + 3} className="team-card">
//                 <div className="team-image" style={{ backgroundImage: member.image ? `url(${member.image})` : 'none' }}></div>
//                 <h3>{member.name}</h3>
//                 <p className="role">{member.role}</p>
//                 <p className="bio">{member.bio}</p>
//                 <div className="social-links">
//                   <a href={member.social.linkedin} aria-label="LinkedIn"><Linkedin size={20} /></a>
//                   <a href={member.social.twitter} aria-label="Twitter"><Twitter size={20} /></a>
//                   <a href={`mailto:${member.social.email}`} aria-label="Email"><Mail size={20} /></a>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* CTA */}
//       <section className="final-cta text-center">
//         <div className="container">
//           <h2>
//             Inspired by our team? Let's collaborate on your next big insight.
//           </h2>
//           <a href="/contact" className="btn btn-primary">
//             Connect With Us <ArrowRight size={28} />
//           </a>
//         </div>
//       </section>

//       {/* Footer - Reused for consistency */}
//       <footer className="footer">
//         <div className="footer-grid">
//           <div>
//             <div className="footer-logo">InSight Research</div>
//           </div>
//           <div>
//             <h4>Services</h4>
//             <ul>
//               <li><a href="/services">Market Research</a></li>
//               <li><a href="/services">Impact Evaluation</a></li>
//               <li><a href="/services">Data Analytics</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Sectors</h4>
//             <ul>
//               <li><a href="/sectors">Education</a></li>
//               <li><a href="/sectors">Public Health</a></li>
//               <li><a href="/sectors">Finance & Development</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Company</h4>
//             <ul>
//               <li><a href="/about">About</a></li>
//               <li><a href="/team">Team</a></li>
//               <li><a href="#">Reports</a></li>
//             </ul>
//           </div>
//           <div>
//             <h4>Contact</h4>
//             <ul>
//               <li><a href="/contact">hello@insightresearch.com</a></li>
//               <li><a href="/contact">Locations</a></li>
//             </ul>
//           </div>
//         </div>
//         <div className="copyright">
//           © 2025 InSight Research. All rights reserved.
//         </div>
//       </footer>
//     </>
//   );
// };

// export default Team;